import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckSquare,
  Bell,
  BellOff,
  Ban,
  Activity,
  Clock,
  Download,
  ExternalLink,
  Globe2,
  GripVertical,
  ImagePlus,
  MessageCircle,
  Lock,
  LogOut,
  Monitor,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Send,
  ShieldCheck,
  Square,
  Trash2,
  X,
} from "lucide-react";
import "../index.css";
import { apiUrl } from "../shared/api";

const STATUS_OPTIONS = ["undetected", "updating", "detected", "frozen"];

const STATUS_CLASSES = {
  undetected: "bg-green-600/15 border-green-500/50 text-green-300",
  updating: "bg-amber-500/15 border-amber-400/50 text-amber-300",
  detected: "bg-red-600/15 border-red-500/50 text-red-300",
  frozen: "bg-gray-500/15 border-gray-400/50 text-gray-300",
};

const STATUS_ROW_CLASSES = {
  undetected: "bg-green-500/[0.04] hover:bg-green-500/[0.08]",
  updating: "bg-amber-500/[0.06] hover:bg-amber-500/[0.1]",
  detected: "bg-red-500/[0.07] hover:bg-red-500/[0.12]",
  frozen: "bg-gray-400/[0.04] hover:bg-gray-400/[0.08]",
};

const CHAT_STATUS_OPTIONS = ["new", "open", "waiting", "resolved"];

const CHAT_STATUS_CLASSES = {
  new: "border-red-400/45 bg-red-500/15 text-red-100",
  open: "border-green-400/35 bg-green-500/10 text-green-100",
  waiting: "border-amber-400/35 bg-amber-500/10 text-amber-100",
  resolved: "border-gray-400/30 bg-gray-500/10 text-gray-100",
};

const QUICK_REPLIES = [
  "Hello, how can we help?",
  "Please send your order ID.",
  "Can you share a screenshot of the issue?",
  "We are checking your request now.",
  "Please join our Discord for setup support: https://discord.gg/HdZBtMFsWA",
];

const VISITOR_RISK_CLASSES = {
  normal: "border-green-400/35 bg-green-500/10 text-green-100",
  watch: "border-yellow-400/35 bg-yellow-500/10 text-yellow-100",
  medium: "border-orange-400/40 bg-orange-500/12 text-orange-100",
  high: "border-red-400/45 bg-red-500/15 text-red-100",
};

const VISITOR_RISK_LABELS = {
  normal: "Normal",
  watch: "Watch",
  medium: "Suspicious",
  high: "High risk",
};

async function apiRequest(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `HTTP ${response.status}`);
  }

  return data;
}

function LinkifiedText({ text }) {
  const parts = String(text || "").split(/(https?:\/\/[^\s<>"']+)/gi);

  return (
    <>
      {parts.map((part, index) => {
        if (!/^https?:\/\//i.test(part)) return <span key={`${part}-${index}`}>{part}</span>;

        return (
          <a
            key={`${part}-${index}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-purple-200 underline decoration-purple-300/60 underline-offset-2 hover:text-purple-100"
          >
            {part}
          </a>
        );
      })}
    </>
  );
}

function ChatAttachment({ attachment }) {
  if (!attachment?.dataUrl) return null;

  return (
    <a href={attachment.dataUrl} target="_blank" rel="noopener noreferrer" className="mt-2 block">
      <img
        src={attachment.dataUrl}
        alt={attachment.name || "Chat attachment"}
        className="max-h-56 w-full rounded-md border border-white/15 object-contain"
      />
    </a>
  );
}

function imageFileToAttachment(file) {
  return new Promise((resolve, reject) => {
    const allowedTypes = ["image/png", "image/jpeg", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      reject(new Error("Only PNG, JPG, WEBP, and GIF images are supported."));
      return;
    }
    if (file.size > 650 * 1024) {
      reject(new Error("Image must be under 650 KB."));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, type: file.type, dataUrl: reader.result });
    reader.onerror = () => reject(new Error("Image could not be read."));
    reader.readAsDataURL(file);
  });
}

function productAssetNameFromFile(file) {
  const allowedExtensions = /\.(png|jpe?g|webp|gif|mp4|webm|ogg|mov|m4v)$/i;
  const isAllowedType = /^(image|video)\//i.test(file.type || "");
  if (!isAllowedType && !allowedExtensions.test(file.name || "")) {
    throw new Error("Only image and video files are supported.");
  }

  const fileName = String(file.name || "").trim();
  if (!fileName || fileName.includes("/") || fileName.includes("\\")) {
    throw new Error("Invalid file name.");
  }

  return fileName;
}

function mergeMultilinePaths(currentValue, nextPaths) {
  return [
    ...String(currentValue || "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean),
    ...nextPaths,
  ].join("\n");
}

function useNoIndex() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Admin Status Panel";

    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);

    return () => {
      document.title = previousTitle;
      meta.remove();
    };
  }, []);
}

function LoginPanel({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      await apiRequest("/api/admin/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      setPassword("");
      onLogin();
    } catch (loginError) {
      setError(loginError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 text-white font-jersey">
      <form onSubmit={submit} className="w-full max-w-md rounded-lg border border-purple-400/25 bg-gray-950/75 p-6 shadow-2xl backdrop-blur-xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-lg border border-purple-400/30 bg-purple-600/15 p-3 text-purple-300">
            <Lock size={26} />
          </div>
          <div>
            <h1 className="text-3xl font-extrabold">Status Admin</h1>
            <p className="text-gray-400 text-lg">Restricted access</p>
          </div>
        </div>

        <label className="block text-lg font-bold text-gray-200" htmlFor="admin-password">
          Password
        </label>
        <input
          id="admin-password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          className="admin-field mt-2 w-full rounded-lg border border-purple-400/25 bg-black/45 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400"
          autoFocus
        />

        {error && (
          <p className="mt-4 rounded-lg border border-red-500/40 bg-red-600/10 px-4 py-3 text-red-200">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !password}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 px-5 py-3 text-xl font-extrabold text-white transition hover:bg-purple-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ShieldCheck size={22} />
          {isSubmitting ? "Checking..." : "Unlock"}
        </button>

        <Link
          to="/"
          className="mt-3 inline-flex w-full items-center justify-center rounded-lg border border-purple-400/25 bg-black/30 px-5 py-3 text-xl font-bold text-purple-100 transition hover:bg-purple-500/15"
        >
          Back to site
        </Link>
      </form>
    </div>
  );
}

function StatusSelect({ value, onChange, disabled }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      className={`admin-field w-full rounded-lg border px-3 py-2 text-lg font-bold outline-none ${STATUS_CLASSES[value] || "bg-gray-900 border-gray-600 text-white"}`}
    >
      {STATUS_OPTIONS.map((status) => (
        <option key={status} value={status} className="bg-gray-950 text-white">
          {status}
        </option>
      ))}
    </select>
  );
}

function statusLabel(status) {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function rowKey(row) {
  return `${row.dataFile}:${row.index}`;
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

function timeAgo(value) {
  const timestamp = Date.parse(value || "");
  if (!Number.isFinite(timestamp)) return "-";
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function durationBetween(startValue, endValue = new Date().toISOString()) {
  const start = Date.parse(startValue || "");
  const end = Date.parse(endValue || "");
  if (!Number.isFinite(start) || !Number.isFinite(end)) return "-";
  const minutes = Math.max(0, Math.floor((end - start) / 60000));
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h${remainingMinutes ? ` ${remainingMinutes}m` : ""}`;
}

function recentEvents(visitor, windowMs) {
  return (Array.isArray(visitor.pageEvents) ? visitor.pageEvents : []).filter((event) => {
    const timestamp = Date.parse(event.at || "");
    return Number.isFinite(timestamp) && Date.now() - timestamp <= windowMs;
  });
}

function playAdminNotification() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(740, context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(980, context.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.22);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.24);
    setTimeout(() => context.close().catch(() => null), 360);
  } catch {
    // Notification audio is best effort.
  }
}

function escapeCsv(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function StatusSummary({ rows, pending, pendingNoteCount }) {
  const counts = useMemo(() => {
    const summary = STATUS_OPTIONS.reduce((nextSummary, status) => {
      nextSummary[status] = 0;
      return nextSummary;
    }, {});

    rows.forEach((row) => {
      const currentStatus = pending[rowKey(row)] || row.status;
      if (summary[currentStatus] !== undefined) summary[currentStatus] += 1;
    });

    return summary;
  }, [pending, rows]);

  const pendingCount = Object.keys(pending).length + pendingNoteCount;

  return (
    <section className="grid grid-cols-2 gap-3 lg:grid-cols-5">
      {STATUS_OPTIONS.map((status) => (
        <div
          key={status}
          className={`rounded-lg border px-4 py-3 ${STATUS_CLASSES[status]}`}
        >
          <p className="text-lg font-bold">{statusLabel(status)}</p>
          <p className="text-3xl font-extrabold">{counts[status]}</p>
        </div>
      ))}
      <div className="rounded-lg border border-purple-400/30 bg-purple-600/10 px-4 py-3 text-purple-100">
        <p className="text-lg font-bold">Pending</p>
        <p className="text-3xl font-extrabold">{pendingCount}</p>
      </div>
    </section>
  );
}

function BulkActions({
  selectedCount,
  visibleCount,
  hasPending,
  statusChangeMessage,
  onApplyStatus,
  onApplyVisibleStatus,
  onStatusChangeMessageChange,
  onClearSelection,
  onSelectVisible,
  onResetPending,
  onUndoLast,
  onExportCsv,
  onValidate,
  onSave,
  isSaving,
  isValidating,
}) {
  return (
    <section className="rounded-lg border border-purple-400/20 bg-gray-950/55 p-4 shadow-xl backdrop-blur-xl">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <p className="text-xl font-extrabold text-white">
            {selectedCount} selected / {visibleCount} visible
          </p>
          <p className="text-base text-gray-400">
            Bulk changes stay pending until you save.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((status) => (
            <button
              key={status}
              type="button"
              disabled={selectedCount === 0 || isSaving}
              onClick={() => onApplyStatus(status)}
              className={`rounded-lg border px-3 py-2 text-base font-bold transition disabled:cursor-not-allowed disabled:opacity-45 ${STATUS_CLASSES[status]}`}
            >
              Set {statusLabel(status)}
            </button>
          ))}
          {STATUS_OPTIONS.map((status) => (
            <button
              key={`visible-${status}`}
              type="button"
              disabled={visibleCount === 0 || isSaving}
              onClick={() => onApplyVisibleStatus(status)}
              className={`rounded-lg border px-3 py-2 text-base font-bold transition disabled:cursor-not-allowed disabled:opacity-45 ${STATUS_CLASSES[status]}`}
            >
              Visible {statusLabel(status)}
            </button>
          ))}
          <button
            type="button"
            disabled={visibleCount === 0 || isSaving}
            onClick={onSelectVisible}
            className="rounded-lg border border-purple-400/25 bg-black/30 px-3 py-2 text-base font-bold text-purple-100 transition hover:bg-purple-500/15 disabled:cursor-not-allowed disabled:opacity-45"
          >
            Select visible
          </button>
          <button
            type="button"
            disabled={selectedCount === 0 || isSaving}
            onClick={onClearSelection}
            className="rounded-lg border border-purple-400/25 bg-black/30 px-3 py-2 text-base font-bold text-purple-100 transition hover:bg-purple-500/15 disabled:cursor-not-allowed disabled:opacity-45"
          >
            Clear selection
          </button>
          <button
            type="button"
            disabled={!hasPending || isSaving}
            onClick={onResetPending}
            className="rounded-lg border border-yellow-400/30 bg-yellow-500/10 px-3 py-2 text-base font-bold text-yellow-100 transition hover:bg-yellow-500/20 disabled:cursor-not-allowed disabled:opacity-45"
          >
            Reset changes
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={onUndoLast}
            className="inline-flex items-center gap-2 rounded-lg border border-orange-400/30 bg-orange-500/10 px-3 py-2 text-base font-bold text-orange-100 transition hover:bg-orange-500/20 disabled:cursor-not-allowed disabled:opacity-45"
          >
            <RotateCcw size={18} />
            Undo last save
          </button>
          <button
            type="button"
            onClick={onExportCsv}
            className="inline-flex items-center gap-2 rounded-lg border border-purple-400/25 bg-black/30 px-3 py-2 text-base font-bold text-purple-100 transition hover:bg-purple-500/15"
          >
            <Download size={18} />
            Export CSV
          </button>
          <button
            type="button"
            disabled={isValidating}
            onClick={onValidate}
            className="rounded-lg border border-purple-400/25 bg-purple-500/10 px-3 py-2 text-base font-bold text-purple-100 transition hover:bg-purple-500/20 disabled:cursor-not-allowed disabled:opacity-45"
          >
            {isValidating ? "Checking..." : "Validate data"}
          </button>
          <button
            type="button"
            disabled={!hasPending || isSaving}
            onClick={onSave}
            className="inline-flex items-center gap-2 rounded-lg border border-green-400/35 bg-green-600/20 px-4 py-2 text-base font-extrabold text-green-100 transition hover:bg-green-600/30 disabled:cursor-not-allowed disabled:opacity-45"
          >
            <Save size={18} />
            {isSaving ? "Saving..." : "Save all changes"}
          </button>
        </div>
      </div>

      <label className="mt-4 grid gap-2 text-base font-bold text-gray-300">
        Discord status update info (optional)
        <textarea
          value={statusChangeMessage}
          onChange={(event) => onStatusChangeMessageChange(event.target.value)}
          rows={2}
          maxLength={900}
          disabled={isSaving}
          placeholder="Example: 3 extra days added"
          className="admin-field w-full resize-y rounded-lg border border-purple-400/20 bg-black/35 px-3 py-2 text-lg text-white outline-none focus:ring-2 focus:ring-purple-400 disabled:cursor-not-allowed disabled:opacity-50"
        />
        <span className="text-sm font-bold text-gray-500">
          Sent to Discord only when saved status changes are applied.
        </span>
      </label>
    </section>
  );
}

function AuditPanel({ rows }) {
  if (rows.length === 0) return null;

  return (
    <section className="rounded-lg border border-purple-400/20 bg-gray-950/55 p-4 shadow-xl backdrop-blur-xl">
      <h2 className="mb-3 text-2xl font-extrabold">Recent changes</h2>
      <div className="space-y-2">
        {rows.map((entry, index) => (
          <div
            key={`${entry.at}-${entry.dataFile}-${entry.index}-${index}`}
            className="rounded-lg border border-purple-400/10 bg-black/25 px-3 py-2 text-base text-gray-200"
          >
            <span className="font-bold text-purple-200">{entry.name}</span>{" "}
            {entry.action === "note_update" ? (
              <span className="text-gray-300">note updated</span>
            ) : entry.action === "undo_status_update" ? (
              <>
                <span className="text-orange-200">undo</span>
                <span className="mx-2 text-gray-500">-&gt;</span>
                <span className="font-bold text-white">{entry.status}</span>
              </>
            ) : (
              <>
                <span className="text-gray-400">{entry.previousStatus}</span>
                <span className="mx-2 text-gray-500">-&gt;</span>
                <span className="font-bold text-white">{entry.status}</span>
              </>
            )}
            <span className="ml-3 text-gray-500">
              {new Date(entry.at).toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function FeaturedProductsPanel({ rows, featuredItems, onFeaturedItemsChange }) {
  const [query, setQuery] = useState("");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const featuredKeys = useMemo(
    () => new Set(featuredItems.map((item) => `${item.dataFile}:${item.index}`)),
    [featuredItems]
  );
  const selectedRows = useMemo(
    () => featuredItems
      .map((item) => rows.find((row) => row.dataFile === item.dataFile && row.index === item.index))
      .filter(Boolean),
    [featuredItems, rows]
  );
  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return rows
      .filter((row) => (
        !normalizedQuery
        || row.name.toLowerCase().includes(normalizedQuery)
        || row.gameName.toLowerCase().includes(normalizedQuery)
        || row.dataFile.toLowerCase().includes(normalizedQuery)
      ))
      .slice(0, 80);
  }, [query, rows]);

  const toggleFeatured = (row) => {
    const key = `${row.dataFile}:${row.index}`;
    if (featuredKeys.has(key)) {
      onFeaturedItemsChange(featuredItems.filter((item) => `${item.dataFile}:${item.index}` !== key));
      return;
    }

    if (featuredItems.length >= 8) {
      setMessage("You can feature up to 8 products.");
      return;
    }

    onFeaturedItemsChange([
      ...featuredItems,
      {
        dataFile: row.dataFile,
        index: row.index,
        name: row.name,
        link: row.link,
      },
    ]);
  };

  const reorderFeaturedItems = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= featuredItems.length || toIndex >= featuredItems.length) return;

    const nextItems = [...featuredItems];
    const [item] = nextItems.splice(fromIndex, 1);
    nextItems.splice(toIndex, 0, item);
    onFeaturedItemsChange(nextItems);
  };

  const handleDragStart = (event, index) => {
    setDraggedIndex(index);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDrop = (event, index) => {
    event.preventDefault();
    const sourceIndex = Number(event.dataTransfer.getData("text/plain"));
    reorderFeaturedItems(Number.isInteger(sourceIndex) ? sourceIndex : draggedIndex, index);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const clearDragState = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const saveFeatured = async () => {
    setIsSaving(true);
    setMessage("");
    try {
      const data = await apiRequest("/api/admin/featured-products", {
        method: "PATCH",
        body: JSON.stringify({ items: featuredItems }),
      });
      onFeaturedItemsChange(data.items || []);
      setMessage(`${(data.items || []).length} popular product${(data.items || []).length > 1 ? "s" : ""} saved.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
      <section className="rounded-lg border border-purple-400/20 bg-gray-950/55 p-4 shadow-xl backdrop-blur-xl">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-2xl font-extrabold text-white">Home popular list</h3>
            <p className="text-base text-gray-400">{featuredItems.length}/8 products selected</p>
          </div>
          <button
            type="button"
            onClick={saveFeatured}
            disabled={isSaving}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-green-400/35 bg-green-600/20 px-4 py-2 text-lg font-extrabold text-green-100 transition hover:bg-green-600/30 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Save size={18} />
            {isSaving ? "Saving..." : "Save popular"}
          </button>
        </div>

        {message && (
          <p className="mt-3 rounded-lg border border-purple-400/20 bg-black/30 px-3 py-2 text-base text-purple-100">
            {message}
          </p>
        )}

        <div className="mt-4 grid gap-2">
          {selectedRows.length === 0 ? (
            <div className="rounded-lg border border-purple-400/15 bg-black/25 px-4 py-5 text-center text-gray-400">
              No popular products selected.
            </div>
          ) : (
            selectedRows.map((row, index) => (
              <div
                key={rowKey(row)}
                draggable
                onDragStart={(event) => handleDragStart(event, index)}
                onDragOver={(event) => handleDragOver(event, index)}
                onDragLeave={() => setDragOverIndex(null)}
                onDrop={(event) => handleDrop(event, index)}
                onDragEnd={clearDragState}
                className={`flex cursor-grab items-center justify-between gap-3 rounded-lg border px-3 py-2 transition active:cursor-grabbing ${
                  dragOverIndex === index && draggedIndex !== index
                    ? "border-purple-300/60 bg-purple-500/18 shadow-[0_0_24px_rgba(168,85,247,0.12)]"
                    : "border-purple-400/15 bg-black/25"
                } ${draggedIndex === index ? "opacity-55" : ""}`}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <GripVertical size={18} className="shrink-0 text-purple-300/70" />
                  <div className="min-w-0">
                  <p className="truncate text-lg font-extrabold text-white">{index + 1}. {row.name}</p>
                  <p className="truncate text-sm font-bold text-gray-400">{row.gameName} / {statusLabel(row.status)}</p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => toggleFeatured(row)}
                    className="rounded-md border border-red-400/25 bg-red-500/10 px-2 py-1 text-sm font-bold text-red-100 transition hover:bg-red-500/20"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="rounded-lg border border-purple-400/20 bg-gray-950/55 p-4 shadow-xl backdrop-blur-xl">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search popular product..."
            className="admin-field w-full rounded-lg border border-purple-400/20 bg-black/40 py-3 pl-11 pr-4 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>

        <div className="mt-4 max-h-[34rem] overflow-y-auto pr-1">
          <div className="grid gap-2">
            {filteredRows.map((row) => {
              const isFeatured = featuredKeys.has(rowKey(row));
              return (
                <button
                  key={rowKey(row)}
                  type="button"
                  onClick={() => toggleFeatured(row)}
                  className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition ${
                    isFeatured
                      ? "border-green-300/45 bg-green-500/12 text-green-100"
                      : "border-purple-400/15 bg-black/25 text-gray-200 hover:border-purple-300/35 hover:bg-purple-600/10"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-lg font-extrabold">{row.name}</span>
                    <span className="block truncate text-sm font-bold opacity-70">{row.gameName} / {row.price || "no price"}</span>
                  </span>
                  {isFeatured ? <CheckSquare size={20} /> : <Square size={20} />}
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

function AdminDashboard({
  rows,
  auditRows,
  featuredItems,
  statusCounts,
  pendingTotal,
  hasPendingChanges,
  onNavigate,
  onRefresh,
}) {
  const popularRows = featuredItems
    .map((item) => rows.find((row) => row.dataFile === item.dataFile && row.index === item.index))
    .filter(Boolean);
  const recentlyChanged = rows
    .filter((row) => row.lastUpdatedAt)
    .sort((a, b) => new Date(b.lastUpdatedAt).getTime() - new Date(a.lastUpdatedAt).getTime())
    .slice(0, 5);
  const updatingRows = rows.filter((row) => row.status === "updating").slice(0, 5);
  const detectedRows = rows.filter((row) => row.status === "detected").slice(0, 5);

  return (
    <div className="space-y-4">
      {hasPendingChanges && (
        <div className="rounded-lg border border-yellow-300/30 bg-yellow-500/10 px-4 py-3 text-lg font-bold text-yellow-100">
          {pendingTotal} unsaved change{pendingTotal > 1 ? "s" : ""} in Status Control.
        </div>
      )}

      <section className="grid gap-4 xl:grid-cols-[1fr_0.9fr]">
        <div className="rounded-lg border border-purple-400/20 bg-gray-950/55 p-4 shadow-xl backdrop-blur-xl">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-2xl font-extrabold text-white">Needs attention</h3>
              <p className="text-base text-gray-400">Products currently updating or detected.</p>
            </div>
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center gap-2 rounded-lg border border-purple-400/25 bg-purple-600/15 px-3 py-2 text-base font-bold text-purple-100 transition hover:bg-purple-600/25"
            >
              <RefreshCw size={17} />
              Refresh
            </button>
          </div>
          <div className="grid gap-2">
            {[...detectedRows, ...updatingRows].length === 0 ? (
              <p className="rounded-lg border border-green-400/20 bg-green-500/10 px-3 py-4 text-center text-green-100">
                No products need attention right now.
              </p>
            ) : (
              [...detectedRows, ...updatingRows].map((row) => (
                <button
                  key={rowKey(row)}
                  type="button"
                  onClick={() => onNavigate("status")}
                  className="flex items-center justify-between gap-3 rounded-lg border border-purple-400/15 bg-black/25 px-3 py-2 text-left transition hover:border-purple-300/35 hover:bg-purple-600/10"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-lg font-extrabold text-white">{row.name}</span>
                    <span className="block truncate text-sm font-bold text-gray-400">{row.gameName}</span>
                  </span>
                  <span className={`shrink-0 rounded-md border px-2 py-1 text-xs font-extrabold uppercase ${STATUS_CLASSES[row.status] || "border-purple-400/20 bg-purple-500/10 text-purple-100"}`}>
                    {statusLabel(row.status)}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="rounded-lg border border-purple-400/20 bg-gray-950/55 p-4 shadow-xl backdrop-blur-xl">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-2xl font-extrabold text-white">Popular home</h3>
              <p className="text-base text-gray-400">{popularRows.length}/8 products configured</p>
            </div>
            <button
              type="button"
              onClick={() => onNavigate("featured")}
              className="rounded-lg border border-green-400/25 bg-green-500/10 px-3 py-2 text-base font-bold text-green-100 transition hover:bg-green-500/20"
            >
              Manage
            </button>
          </div>
          <div className="grid gap-2">
            {popularRows.length === 0 ? (
              <p className="rounded-lg border border-purple-400/15 bg-black/25 px-3 py-4 text-center text-gray-400">
                No popular products configured.
              </p>
            ) : (
              popularRows.slice(0, 5).map((row, index) => (
                <div key={rowKey(row)} className="flex items-center justify-between gap-3 rounded-lg border border-purple-400/15 bg-black/25 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-lg font-extrabold text-white">{index + 1}. {row.name}</p>
                    <p className="truncate text-sm font-bold text-gray-400">{row.gameName}</p>
                  </div>
                  <span className={`shrink-0 rounded-md border px-2 py-1 text-xs font-extrabold uppercase ${STATUS_CLASSES[row.status] || "border-purple-400/20 bg-purple-500/10 text-purple-100"}`}>
                    {statusLabel(row.status)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-lg border border-purple-400/20 bg-gray-950/55 p-4 shadow-xl backdrop-blur-xl">
          <h3 className="mb-3 text-2xl font-extrabold text-white">Recently changed</h3>
          <div className="grid gap-2">
            {recentlyChanged.length === 0 ? (
              <p className="rounded-lg border border-purple-400/15 bg-black/25 px-3 py-4 text-center text-gray-400">No recent product updates.</p>
            ) : (
              recentlyChanged.map((row) => (
                <div key={rowKey(row)} className="rounded-lg border border-purple-400/15 bg-black/25 px-3 py-2">
                  <p className="truncate text-lg font-extrabold text-white">{row.name}</p>
                  <p className="text-sm font-bold text-gray-400">{row.gameName} / {timeAgo(row.lastUpdatedAt)}</p>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="rounded-lg border border-purple-400/20 bg-gray-950/55 p-4 shadow-xl backdrop-blur-xl">
          <h3 className="mb-3 text-2xl font-extrabold text-white">Latest audit</h3>
          <div className="grid gap-2">
            {auditRows.length === 0 ? (
              <p className="rounded-lg border border-purple-400/15 bg-black/25 px-3 py-4 text-center text-gray-400">No audit entries yet.</p>
            ) : (
              auditRows.slice(0, 5).map((entry, index) => (
                <div key={`${entry.at}-${entry.action}-${index}`} className="rounded-lg border border-purple-400/15 bg-black/25 px-3 py-2">
                  <p className="truncate text-lg font-extrabold text-white">{entry.name || entry.action}</p>
                  <p className="text-sm font-bold text-gray-400">
                    {entry.action} / {entry.at ? new Date(entry.at).toLocaleString() : "-"}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function ActiveVisitorsPanel() {
  const [visitorSummary, setVisitorSummary] = useState({ onlineCount: 0, recentCount: 0, globalStats: null, visitors: [] });
  const [analyticsPeriod, setAnalyticsPeriod] = useState("day");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });

  const loadVisitors = useCallback(async () => {
    const params = new URLSearchParams({ period: analyticsPeriod });
    if (analyticsPeriod === "custom") {
      if (customRange.start) params.set("start", customRange.start);
      if (customRange.end) params.set("end", customRange.end);
    }
    const data = await apiRequest(`/api/admin/visitors?${params.toString()}`);
    setVisitorSummary({
      onlineCount: data.onlineCount || 0,
      recentCount: data.recentCount || 0,
      globalStats: data.globalStats || null,
      visitors: data.visitors || [],
    });
  }, [analyticsPeriod, customRange.end, customRange.start]);

  useEffect(() => {
    loadVisitors().catch(() => null);
    const intervalId = setInterval(() => loadVisitors().catch(() => null), 5000);
    return () => clearInterval(intervalId);
  }, [loadVisitors]);

  const activeVisitorRows = visitorSummary.visitors.slice(0, 12);
  const flaggedCount = useMemo(
    () => visitorSummary.visitors.filter((visitor) => visitor.risk?.level && visitor.risk.level !== "normal").length,
    [visitorSummary.visitors]
  );

  return (
    <section className="rounded-lg border border-purple-400/20 bg-gray-950/60 p-4 shadow-xl shadow-black/20 backdrop-blur-xl">
      <div className="mb-4 flex flex-col gap-3 border-b border-purple-400/15 pb-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="inline-flex items-center gap-2 text-2xl font-extrabold text-purple-100">
            <Monitor size={21} />
            Visitor dashboard
          </h3>
          <p className="text-base text-gray-400">
            Global analytics and live sessions, refreshed every 5 seconds.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="rounded-lg border border-green-400/25 bg-green-500/10 px-3 py-2 text-sm font-extrabold uppercase text-green-100">
            {visitorSummary.onlineCount} online
          </span>
          <span className={`rounded-lg border px-3 py-2 text-sm font-extrabold uppercase ${flaggedCount ? "border-yellow-400/25 bg-yellow-500/10 text-yellow-100" : "border-purple-400/20 bg-purple-500/10 text-purple-100"}`}>
            {flaggedCount} flagged
          </span>
          <span className="rounded-lg border border-white/10 bg-black/25 px-3 py-2 text-sm font-extrabold uppercase text-gray-300">
            {visitorSummary.recentCount} tracked 24h
          </span>
        </div>
      </div>

      <AnalyticsPeriodControls
        period={analyticsPeriod}
        customRange={customRange}
        onPeriodChange={setAnalyticsPeriod}
        onCustomRangeChange={setCustomRange}
      />

      <GlobalVisitorStats stats={visitorSummary.globalStats} period={analyticsPeriod} />

      <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-2">
        <h4 className="text-2xl font-extrabold text-white">Live sessions</h4>
        <p className="text-sm font-bold uppercase text-gray-500">{activeVisitorRows.length} shown</p>
      </div>

      {activeVisitorRows.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-3 text-base text-gray-400">
          No active visitors detected yet.
        </div>
      ) : (
        <div className="grid gap-3 xl:grid-cols-2">
          {activeVisitorRows.map((visitor) => (
            <VisitorCard key={`visitor-${visitor.id}`} visitor={visitor} />
          ))}
        </div>
      )}
    </section>
  );
}

function AnalyticsMetricCard({ icon, title, primaryLabel, primaryValue, change, footerLabel, footerValue, children, tone }) {
  const tones = {
    blue: "border-purple-400/20 bg-purple-500/10 text-purple-100",
    green: "border-green-400/20 bg-green-500/10 text-green-100",
    purple: "border-purple-400/20 bg-purple-500/10 text-purple-100",
    yellow: "border-yellow-400/25 bg-yellow-500/10 text-yellow-100",
  };
  const changeValue = Number(change || 0);
  const changeClass = changeValue > 0 ? "text-green-300" : changeValue < 0 ? "text-red-300" : "text-gray-400";

  return (
    <div className={`min-w-0 rounded-lg border px-5 py-4 ${tones[tone] || tones.blue}`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-base font-extrabold text-white">
          {icon}
          {title}
        </div>
        {change !== undefined && (
          <span className={`text-sm font-extrabold ${changeClass}`}>
            {changeValue.toFixed(1)}%
          </span>
        )}
      </div>
      {children || (
        <>
          <p className="text-sm font-bold uppercase text-gray-400">{primaryLabel}</p>
          <p className="mt-1 text-4xl font-extrabold text-white">{primaryValue}</p>
          <div className="mt-4 border-t border-white/10 pt-3">
            <p className="text-sm font-bold text-gray-400">{footerLabel}</p>
            <p className="mt-1 text-2xl font-extrabold text-white">{footerValue}</p>
          </div>
        </>
      )}
    </div>
  );
}

function AnalyticsPeriodControls({ period, customRange, onPeriodChange, onCustomRangeChange }) {
  const options = [
    { id: "day", label: "Day" },
    { id: "week", label: "Week" },
    { id: "month", label: "Month" },
    { id: "custom", label: "Custom" },
  ];

  return (
    <div className="mb-4 rounded-lg border border-white/10 bg-black/20 p-3">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => onPeriodChange(option.id)}
              className={`rounded-lg border px-4 py-2 text-base font-extrabold transition ${
                period === option.id
                  ? "border-purple-300/60 bg-purple-600/30 text-white"
                  : "border-purple-400/20 bg-purple-600/10 text-purple-100 hover:bg-purple-600/20"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {period === "custom" && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label className="flex items-center gap-2 text-sm font-extrabold uppercase text-gray-400">
              From
              <input
                type="date"
                value={customRange.start}
                onChange={(event) => onCustomRangeChange((current) => ({ ...current, start: event.target.value }))}
                className="admin-field rounded-lg border border-purple-400/25 bg-black/45 px-3 py-2 text-base text-white outline-none"
              />
            </label>
            <label className="flex items-center gap-2 text-sm font-extrabold uppercase text-gray-400">
              To
              <input
                type="date"
                value={customRange.end}
                onChange={(event) => onCustomRangeChange((current) => ({ ...current, end: event.target.value }))}
                className="admin-field rounded-lg border border-purple-400/25 bg-black/45 px-3 py-2 text-base text-white outline-none"
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

function GlobalVisitorStats({ stats, period }) {
  if (!stats) return null;
  const dashboard = stats.dashboard || {};
  const range = dashboard.range || {};
  const visitors = dashboard.visitors || {};
  const chats = dashboard.chats || {};
  const pageViews = dashboard.pageViews || {};
  const reporting = dashboard.reporting || {};
  const primaryLabel = range.label || (period === "week" ? "Last 7 days" : period === "month" ? "Last 30 days" : "Today");

  return (
    <div className="mb-5 rounded-lg border border-purple-400/20 bg-purple-500/[0.05] p-3">
      <div className="mb-3 flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
        <div>
          <h4 className="text-2xl font-extrabold text-purple-100">Analytics overview</h4>
          <p className="text-base text-gray-400">IP-based visitors, chat response and reporting health.</p>
        </div>
        <p className="text-sm font-bold uppercase text-gray-500">{primaryLabel} / refreshed every 5 seconds</p>
      </div>

      <div className="grid gap-3 xl:grid-cols-4">
        <AnalyticsMetricCard
          icon={<Monitor size={18} />}
          title="Visitors"
          primaryLabel={primaryLabel}
          primaryValue={visitors.current || 0}
          change={visitors.change || 0}
          footerLabel="Last 7 days"
          footerValue={`${visitors.last7Days || 0} / ${visitors.online || 0} online`}
          tone="green"
        />
        <AnalyticsMetricCard
          icon={<MessageCircle size={18} />}
          title="Chats"
          change={chats.change || 0}
          tone="purple"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-sm font-bold uppercase text-gray-400">Answered</p>
              <p className="mt-1 text-4xl font-extrabold text-white">{chats.answered || 0}</p>
            </div>
            <div>
              <p className="text-sm font-bold uppercase text-gray-400">Missed</p>
              <p className="mt-1 text-4xl font-extrabold text-white">{chats.missed || 0}</p>
            </div>
          </div>
          <div className="mt-4 border-t border-white/10 pt-3">
            <p className="text-sm font-bold text-gray-400">Last 7 days</p>
            <div className="mt-2 grid grid-cols-2 gap-3 text-sm font-bold text-gray-300">
              <span>Answered: {chats.last7DaysAnswered || 0}</span>
              <span>Missed: {chats.last7DaysMissed || 0}</span>
            </div>
          </div>
        </AnalyticsMetricCard>
        <AnalyticsMetricCard
          icon={<Activity size={18} />}
          title="Page Views"
          primaryLabel={primaryLabel}
          primaryValue={pageViews.current || 0}
          change={pageViews.change || 0}
          footerLabel="Last 7 days"
          footerValue={`${pageViews.last7Days || 0} / ${pageViews.activeNow || 0} active`}
          tone="blue"
        />
        <AnalyticsMetricCard icon={<Globe2 size={18} />} title="Reporting" tone="yellow">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-bold uppercase text-gray-400">Positive Sentiment</p>
              <p className="text-2xl font-extrabold text-white">{(reporting.positiveSentiment || 0).toFixed(1)}%</p>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-3">
              <p className="text-sm font-bold uppercase text-gray-400">Engagement</p>
              <p className="text-2xl font-extrabold text-white">{(reporting.engagement || 0).toFixed(1)}%</p>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-3">
              <p className="text-sm font-bold uppercase text-gray-400">Availability</p>
              <p className="text-2xl font-extrabold text-white">{(reporting.availability ?? 100).toFixed(1)}%</p>
            </div>
          </div>
        </AnalyticsMetricCard>
      </div>
    </div>
  );
}

function VisitorCard({ visitor }) {
  const riskLevel = visitor.risk?.level || "normal";
  const riskClass = VISITOR_RISK_CLASSES[riskLevel] || VISITOR_RISK_CLASSES.normal;
  const riskReasons = Array.isArray(visitor.risk?.reasons) ? visitor.risk.reasons : [];
  const recentPages = [...new Map(
    recentEvents(visitor, 5 * 60 * 1000)
      .reverse()
      .map((event) => [event.page, event])
  ).values()].slice(0, 5);
  const dataFiles = Array.isArray(visitor.dataFiles) ? visitor.dataFiles.slice(-5).reverse() : [];

  return (
    <div
      className={`grid gap-4 rounded-lg border bg-black/35 px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ${
        riskLevel === "high" ? "border-red-400/35" : riskLevel === "medium" ? "border-orange-400/30" : riskLevel === "watch" ? "border-yellow-400/25" : "border-green-400/20"
      }`}
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${visitor.online ? "bg-green-400 shadow-[0_0_10px_rgba(74,222,128,0.8)]" : "bg-amber-400"}`} />
            <p className="text-sm font-extrabold uppercase tracking-normal text-green-200">Online</p>
            <span className="rounded border border-white/10 bg-white/5 px-2 py-0.5 text-xs font-bold text-gray-200">
              {timeAgo(visitor.lastSeenAt)}
            </span>
          </div>
          <p className="truncate text-xl font-extrabold text-white">{visitor.pageLabel || visitor.page || "site"}</p>
          {visitor.pageLabel && visitor.pageLabel !== visitor.page && (
            <p className="truncate text-sm font-bold text-gray-500">{visitor.page}</p>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          <span className={`rounded border px-2 py-0.5 text-xs font-extrabold uppercase ${riskClass}`}>
            {VISITOR_RISK_LABELS[riskLevel] || "Normal"} {visitor.risk?.score ? `${visitor.risk.score}` : ""}
          </span>
          <span className="rounded border border-purple-400/20 bg-purple-500/10 px-2 py-0.5 text-xs font-bold text-purple-100">
            {visitor.hits || 0} hits
          </span>
          <span className="rounded border border-purple-400/20 bg-purple-500/10 px-2 py-0.5 text-xs font-bold text-purple-100">
            {recentEvents(visitor, 60 * 1000).length} views / 60s
          </span>
          {visitor.dataFiles?.length > 0 && (
            <span className="rounded border border-purple-400/25 bg-purple-500/10 px-2 py-0.5 text-xs font-bold text-purple-100">
              {visitor.dataFiles.length} data files
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-3 rounded-lg border border-white/10 bg-black/20 p-3 md:grid-cols-4">
        <VisitorMetric label="IP" value={visitor.ip || "Unknown"} />
        <VisitorMetric label="Country" value={visitor.country || "Unknown"} />
        <VisitorMetric label="Browser" value={visitor.browser || "Unknown browser"} />
        <VisitorMetric label="Duration" value={durationBetween(visitor.firstSeenAt, visitor.lastSeenAt)} />
        <VisitorMetric label="Screen" value={visitor.screen || "Unknown"} />
        <VisitorMetric label="Timezone" value={visitor.timezone || "Unknown"} />
        <VisitorMetric label="Platform" value={visitor.platform || "Unknown"} />
        <VisitorMetric label="Language" value={visitor.language || "Unknown"} />
      </div>

      {riskReasons.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {riskReasons.map((reason) => (
            <span key={reason} className="rounded border border-yellow-400/20 bg-yellow-500/10 px-2 py-0.5 text-xs font-bold text-yellow-100">
              {reason}
            </span>
          ))}
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-3">
        <VisitorDetailList
          title="Recent pages"
          emptyText="No recent page path"
          items={recentPages.map((event) => ({
            key: `${event.at}-${event.page}`,
            main: event.page || "/",
            meta: `${timeAgo(event.at)}${event.dataRequestCount ? ` | ${event.dataRequestCount} data requests` : ""}`,
          }))}
        />
        <VisitorDetailList
          title="Source"
          emptyText="No referrer"
          items={[
            visitor.referrer
              ? { key: "referrer", main: visitor.referrer, meta: "referrer" }
              : null,
          ].filter(Boolean)}
        />
        <VisitorDetailList
          title="Data files"
          emptyText="No data files loaded"
          items={dataFiles.map((file) => ({
            key: file,
            main: file.replace(/^\/data\//, ""),
            meta: "catalog data",
          }))}
        />
      </div>
    </div>
  );
}

function VisitorMetric({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-extrabold uppercase text-gray-500">{label}</p>
      <p className="truncate text-sm font-bold text-gray-200">{value}</p>
    </div>
  );
}

function VisitorDetailList({ title, emptyText, items }) {
  return (
    <div className="min-w-0 rounded-lg border border-white/10 bg-black/25 px-3 py-2">
      <p className="mb-2 text-xs font-extrabold uppercase text-gray-500">{title}</p>
      {items.length === 0 ? (
        <p className="text-sm font-bold text-gray-500">{emptyText}</p>
      ) : (
        <div className="space-y-1.5">
          {items.slice(0, 5).map((item) => (
            <div key={item.key} className="min-w-0">
              <p className="truncate text-sm font-bold text-gray-200">{item.main}</p>
              <p className="truncate text-xs font-bold text-gray-500">{item.meta}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminChatPanel() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [activeSession, setActiveSession] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [notificationsMuted, setNotificationsMuted] = useState(false);
  const [reply, setReply] = useState("");
  const [replyAttachment, setReplyAttachment] = useState(null);
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const previousUnreadRef = useRef(0);

  const loadSessions = useCallback(async () => {
    const data = await apiRequest("/api/admin/chat/sessions");
    setSessions(data.sessions || []);
    if (!activeId && (data.sessions || []).length > 0) {
      setActiveId(data.sessions[0].id);
    }
  }, [activeId]);

  const loadActiveSession = useCallback(async () => {
    if (!activeId) {
      setActiveSession(null);
      return;
    }

    const data = await apiRequest(`/api/admin/chat/${activeId}`);
    setActiveSession(data.session);
  }, [activeId]);

  useEffect(() => {
    loadSessions().catch(() => null);
    const intervalId = setInterval(() => loadSessions().catch(() => null), 4000);
    return () => clearInterval(intervalId);
  }, [loadSessions]);

  useEffect(() => {
    const unread = sessions.reduce((total, session) => total + (session.staffUnread || 0), 0);
    const previousUnread = previousUnreadRef.current;
    previousUnreadRef.current = unread;

    if (unread > previousUnread && previousUnread !== 0 && !notificationsMuted) {
      playAdminNotification();
    }

    const previousTitle = document.title.replace(/^\(\d+\)\s+/, "");
    document.title = unread > 0 ? `(${unread}) ${previousTitle}` : previousTitle;
    return () => {
      document.title = document.title.replace(/^\(\d+\)\s+/, "");
    };
  }, [notificationsMuted, sessions]);

  useEffect(() => {
    loadActiveSession().catch(() => null);
    if (!activeId) return undefined;

    const intervalId = setInterval(() => loadActiveSession().catch(() => null), 2500);
    return () => clearInterval(intervalId);
  }, [activeId, loadActiveSession]);

  useEffect(() => () => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  }, []);

  const sendStaffTyping = async (typing) => {
    if (!activeId) return;
    try {
      await apiRequest(`/api/admin/chat/${activeId}/typing`, {
        method: "POST",
        body: JSON.stringify({ typing }),
      });
    } catch {
      // Typing presence is best effort and should not block replies.
    }
  };

  const handleReplyChange = (event) => {
    const value = event.target.value;
    setReply(value);
    if (!activeId) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (value.trim()) {
      const now = Date.now();
      if (now - lastTypingSentRef.current > 1200) {
        lastTypingSentRef.current = now;
        sendStaffTyping(true);
      }
      typingTimeoutRef.current = setTimeout(() => sendStaffTyping(false), 1800);
    } else {
      sendStaffTyping(false);
    }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    const text = reply.trim();
    if (!activeId || (!text && !replyAttachment)) return;

    setIsSending(true);
    setMessage("");
    try {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      await sendStaffTyping(false);

      const data = await apiRequest(`/api/admin/chat/${activeId}/message`, {
        method: "POST",
        body: JSON.stringify({
          message: text,
          ...(replyAttachment ? { attachment: replyAttachment } : {}),
        }),
      });
      setActiveSession(data.session);
      setReply("");
      setReplyAttachment(null);
      await loadSessions().catch(() => null);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleReplyImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setMessage("");
    try {
      setReplyAttachment(await imageFileToAttachment(file));
    } catch (error) {
      setMessage(error.message);
    }
  };

  const patchChatSession = async (updates) => {
    if (!activeId) return;

    setMessage("");
    try {
      const data = await apiRequest(`/api/admin/chat/${activeId}`, {
        method: "PATCH",
        body: JSON.stringify(updates),
      });
      setActiveSession(data.session);
      await loadSessions().catch(() => null);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const setChatStatus = async (status) => {
    await patchChatSession({ status });
  };

  const toggleBlocked = async () => {
    await patchChatSession({ blocked: !activeSession?.blocked });
  };

  const insertQuickReply = (text) => {
    setReply((current) => (current ? `${current}\n${text}` : text));
  };

  const requestDeleteActiveSession = () => {
    if (!activeId || !activeSession) return;

    setDeleteCandidate({
      id: activeId,
      name: activeSession.name || "Visitor",
      messageCount: (activeSession.messages || []).length,
      lastMessage: (activeSession.messages || []).slice(-1)[0]?.text || "",
    });
  };

  const deleteActiveSession = async () => {
    if (!deleteCandidate?.id) return;

    setIsDeleting(true);
    setMessage("");
    try {
      await apiRequest(`/api/admin/chat/${deleteCandidate.id}`, { method: "DELETE" });
      setActiveId("");
      setActiveSession(null);
      setDeleteCandidate(null);
      const data = await apiRequest("/api/admin/chat/sessions");
      const nextSessions = data.sessions || [];
      setSessions(nextSessions);
      if (nextSessions.length > 0) {
        setActiveId(nextSessions[0].id);
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const unreadCount = sessions.reduce((total, session) => total + (session.staffUnread || 0), 0);
  const activeSessions = sessions.filter((session) => session.status !== "resolved");
  const waitingCount = sessions.filter((session) => session.status === "waiting").length;
  const chatOnlineCount = sessions.filter((session) => session.online).length;
  const filteredSessions = sessions.filter((session) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "active") return session.status !== "resolved";
    return session.status === statusFilter;
  });
  const messages = activeSession?.messages || [];

  return (
    <section className="rounded-lg border border-purple-400/20 bg-gray-950/55 p-4 shadow-xl backdrop-blur-xl">
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="inline-flex items-center gap-2 text-3xl font-extrabold">
            <MessageCircle size={24} />
            Live chat
          </h2>
          <p className="text-lg text-gray-400">
            {activeSessions.length} active / {unreadCount} unread / {chatOnlineCount} chat online / {waitingCount} waiting
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setNotificationsMuted((current) => !current)}
            className="inline-flex items-center gap-2 rounded-lg border border-purple-400/30 bg-black/25 px-4 py-2 text-lg font-bold text-purple-100 transition hover:bg-purple-600/20"
          >
            {notificationsMuted ? <BellOff size={18} /> : <Bell size={18} />}
            {notificationsMuted ? "Muted" : "Alerts on"}
          </button>
          <button
            type="button"
            onClick={() => {
              loadSessions().catch(() => null);
              loadActiveSession().catch(() => null);
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-purple-400/30 bg-purple-600/15 px-4 py-2 text-lg font-bold text-purple-200 transition hover:bg-purple-600/25"
          >
            <RefreshCw size={18} />
            Refresh chat
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {["active", ...CHAT_STATUS_OPTIONS, "all"].map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setStatusFilter(status)}
            className={`rounded-lg border px-3 py-2 text-base font-bold transition ${
              statusFilter === status
                ? "border-purple-300/60 bg-purple-600/30 text-white"
                : "border-purple-400/20 bg-black/25 text-purple-100 hover:bg-purple-500/15"
            }`}
          >
            {status === "active" ? "Active" : status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="max-h-[520px] overflow-y-auto rounded-lg border border-purple-400/15 bg-black/20">
          {filteredSessions.length === 0 ? (
            <div className="p-4 text-lg text-gray-400">No chat conversations yet.</div>
          ) : (
            filteredSessions.map((session) => (
              <button
                key={session.id}
                type="button"
                onClick={() => setActiveId(session.id)}
                className={`block w-full border-b border-purple-400/10 px-4 py-3 text-left transition hover:bg-purple-500/10 ${
                  activeId === session.id ? "bg-purple-600/15" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xl font-bold text-white">{session.name || "Visitor"}</p>
                  <div className="flex items-center gap-1">
                    <span className={`h-2.5 w-2.5 rounded-full ${session.online ? "bg-green-400 shadow-[0_0_10px_rgba(74,222,128,0.8)]" : "bg-gray-500"}`} />
                    {session.staffUnread > 0 && (
                      <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold text-white">
                        {session.staffUnread}
                      </span>
                    )}
                  </div>
                </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <span className={`rounded border px-2 py-0.5 text-xs font-bold ${CHAT_STATUS_CLASSES[session.status] || CHAT_STATUS_CLASSES.open}`}>
                      {session.status || "open"}
                    </span>
                    {session.blocked && (
                      <span className="rounded border border-red-400/40 bg-red-500/15 px-2 py-0.5 text-xs font-bold text-red-100">blocked</span>
                    )}
                    {session.online && (
                      <span className="rounded border border-green-400/35 bg-green-500/10 px-2 py-0.5 text-xs font-bold text-green-100">online</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{session.page || "site"}</p>
                  <p className="text-xs text-gray-600">{session.ip || "Unknown IP"} / {session.browser || "Unknown browser"} / {session.country || "Unknown"}</p>
                  {session.customerTyping && <p className="text-sm text-green-300">Customer is typing...</p>}
                  <p className="truncate text-base text-gray-300">
                    {session.lastMessage?.text || session.lastMessage?.attachment?.name || "Image attached"}
                  </p>
                  <p className="mt-1 inline-flex items-center gap-1 text-xs text-gray-600">
                    <Clock size={12} />
                    {timeAgo(session.lastActivityAt || session.updatedAt)} / {session.messageCount || 0} msg
                  </p>
                </button>
            ))
          )}
        </div>

        <div className="flex min-h-[520px] flex-col rounded-lg border border-purple-400/15 bg-black/20">
          {activeSession ? (
            <>
              <div className="flex flex-col gap-2 border-b border-purple-400/15 px-4 py-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-2xl font-extrabold">{activeSession.name || "Visitor"}</p>
                  <p className="text-sm text-gray-500">{activeSession.page || "site"} / {activeSession.status}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className={`rounded border px-2 py-1 text-xs font-bold ${activeSession.online ? "border-green-400/35 bg-green-500/10 text-green-100" : "border-gray-400/25 bg-gray-500/10 text-gray-200"}`}>
                      {activeSession.online ? "online" : "offline"}
                    </span>
                    <span className="rounded border border-purple-400/25 bg-purple-500/10 px-2 py-1 text-xs font-bold text-purple-100">
                      last activity {timeAgo(activeSession.lastActivityAt || activeSession.updatedAt)}
                    </span>
                    {activeSession.blocked && (
                      <span className="rounded border border-red-400/40 bg-red-500/15 px-2 py-1 text-xs font-bold text-red-100">blocked</span>
                    )}
                  </div>
                  {activeSession.customerTyping && (
                    <p className="text-sm font-bold text-green-300">Customer is typing...</p>
                  )}
                  <div className="mt-2 grid gap-1 text-sm text-gray-400 md:grid-cols-2">
                    <span>IP: {activeSession.ip || "Unknown"}</span>
                    <span>Country: {activeSession.country || "Unknown"}</span>
                    <span className="inline-flex items-center gap-1">
                      <Monitor size={14} />
                      {activeSession.browser || "Unknown browser"}
                    </span>
                    <span>Lang: {activeSession.language || "Unknown"}</span>
                    <span>First seen: {formatDate(activeSession.createdAt)}</span>
                    <span>Messages: {activeSession.messageCount || messages.length}</span>
                    <span className="md:col-span-2 break-all">First page: {activeSession.firstPage || activeSession.page || "site"}</span>
                    <span className="md:col-span-2 break-all">UA: {activeSession.userAgent || "Unknown"}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {CHAT_STATUS_OPTIONS.map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setChatStatus(status)}
                      className={`rounded-lg border px-3 py-2 text-base font-bold hover:bg-white/10 ${CHAT_STATUS_CLASSES[status]}`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={toggleBlocked}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-base font-bold hover:bg-white/10 ${
                      activeSession.blocked
                        ? "border-green-400/30 bg-green-500/10 text-green-100"
                        : "border-red-500/30 bg-red-600/10 text-red-100"
                    }`}
                  >
                    <Ban size={16} />
                    {activeSession.blocked ? "Unblock" : "Block"}
                  </button>
                  <button
                    type="button"
                    onClick={requestDeleteActiveSession}
                    className="inline-flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-600/10 px-3 py-2 text-base font-bold text-red-100 hover:bg-red-600/20"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto p-4">
                {messages.map((chatMessage) => (
                  <div key={chatMessage.id} className={`flex ${chatMessage.from === "staff" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-lg border px-3 py-2 text-base ${
                      chatMessage.from === "staff"
                        ? "border-purple-400/30 bg-purple-600/25 text-purple-50"
                        : "border-green-400/25 bg-green-500/15 text-green-50"
                    }`}>
                      {chatMessage.text && <p className="whitespace-pre-wrap break-words"><LinkifiedText text={chatMessage.text} /></p>}
                      <ChatAttachment attachment={chatMessage.attachment} />
                      <p className="mt-1 text-xs opacity-60">
                        {formatDate(chatMessage.at)}
                        {chatMessage.from === "staff" && (
                          <span className="ml-2">{chatMessage.seenByCustomerAt ? "Seen" : "Unread"}</span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <form onSubmit={sendReply} className="border-t border-purple-400/15 p-3">
                <div className="mb-3 flex flex-wrap gap-2">
                  {QUICK_REPLIES.map((quickReply) => (
                    <button
                      key={quickReply}
                      type="button"
                      onClick={() => insertQuickReply(quickReply)}
                      className="rounded-lg border border-purple-400/20 bg-black/30 px-3 py-1.5 text-sm font-bold text-purple-100 transition hover:bg-purple-500/15"
                    >
                      {quickReply.length > 36 ? `${quickReply.slice(0, 36)}...` : quickReply}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    value={reply}
                    onChange={handleReplyChange}
                    placeholder={activeSession.blocked ? "Conversation is blocked" : "Reply to customer..."}
                    maxLength={1000}
                    disabled={activeSession.blocked}
                    className="admin-field min-w-0 flex-1 rounded-lg border border-purple-400/20 bg-black/40 px-3 py-2 text-lg text-white outline-none focus:ring-2 focus:ring-purple-400"
                  />
                  <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleReplyImageChange} className="hidden" />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={activeSession.blocked}
                    className="inline-flex items-center gap-2 rounded-lg border border-purple-400/30 bg-black/35 px-3 py-2 text-lg font-bold text-purple-100 transition hover:bg-purple-500/15"
                    aria-label="Attach image"
                  >
                    <ImagePlus size={18} />
                  </button>
                  <button
                    type="submit"
                    disabled={activeSession.blocked || isSending || (!reply.trim() && !replyAttachment)}
                    className="inline-flex items-center gap-2 rounded-lg border border-purple-400/35 bg-purple-600 px-4 py-2 text-lg font-bold text-white transition hover:bg-purple-500 disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    <Send size={18} />
                    Send
                  </button>
                </div>
                {replyAttachment && (
                  <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-purple-400/20 bg-black/30 px-3 py-2 text-sm text-purple-100">
                    <span className="truncate">{replyAttachment.name}</span>
                    <button type="button" onClick={() => setReplyAttachment(null)} className="text-purple-200 hover:text-white">
                      Remove
                    </button>
                  </div>
                )}
                {message && <p className="mt-2 text-base text-red-200">{message}</p>}
              </form>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-6 text-xl text-gray-400">
              Select a conversation.
            </div>
          )}
        </div>
      </div>

      {deleteCandidate && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-chat-title"
          onClick={() => !isDeleting && setDeleteCandidate(null)}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-xl border border-red-400/30 bg-gray-950 text-white shadow-[0_24px_90px_rgba(220,38,38,0.24)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-red-400/20 bg-red-600/10 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg border border-red-400/35 bg-red-600/15 p-3 text-red-200 shadow-[0_0_28px_rgba(239,68,68,0.18)]">
                  <Trash2 size={24} />
                </div>
                <div>
                  <p id="delete-chat-title" className="text-2xl font-extrabold text-red-100">
                    Delete conversation?
                  </p>
                  <p className="text-base font-bold text-gray-400">
                    This removes the chat from the staff panel.
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => setDeleteCandidate(null)}
                className="rounded-lg border border-white/10 bg-black/25 p-2 text-gray-300 transition hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Close delete confirmation"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              <div className="rounded-lg border border-purple-400/15 bg-black/30 px-4 py-3">
                <p className="text-sm font-bold uppercase text-gray-500">Conversation</p>
                <p className="mt-1 text-xl font-extrabold text-purple-100">{deleteCandidate.name}</p>
                <p className="mt-1 text-base text-gray-400">
                  {deleteCandidate.messageCount} message{deleteCandidate.messageCount > 1 ? "s" : ""} in this thread
                </p>
                {deleteCandidate.lastMessage && (
                  <p className="mt-3 truncate rounded-md border border-white/10 bg-gray-900/70 px-3 py-2 text-base text-gray-300">
                    {deleteCandidate.lastMessage}
                  </p>
                )}
              </div>

              <p className="text-base leading-relaxed text-gray-300">
                Deleting is permanent for this local chat store. Close the conversation instead if you only want to hide it from active support.
              </p>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => setDeleteCandidate(null)}
                  className="rounded-lg border border-purple-400/25 bg-black/30 px-5 py-3 text-lg font-bold text-purple-100 transition hover:bg-purple-500/15 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Keep conversation
                </button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={deleteActiveSession}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-400/40 bg-red-600/25 px-5 py-3 text-lg font-extrabold text-red-50 transition hover:bg-red-600/35 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Trash2 size={18} />
                  {isDeleting ? "Deleting..." : "Delete forever"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function slugFromName(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "");
}

function pageNameFromName(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "";
}

const emptyProductForm = {
  dataFile: "",
  name: "",
  catalogName: "",
  pageName: "",
  slug: "",
  status: "frozen",
  os: ["10", "11"],
  requirements: {
    windowsVersions: "",
    platform: "PC",
    cpu: "Intel and AMD",
    gpu: "Nvidia and AMD",
    gameMode: "Fullscreen/Windowed",
    spoofer: "None",
  },
  logo: "",
  plans: [
    { plan: "1 Day", price: "$0", url: "" },
    { plan: "7 Days", price: "$0", url: "" },
    { plan: "30 Days", price: "$0", url: "" },
  ],
  featureBlocks: [
    { title: "AIMBOT", text: "Smooth aim\nPrediction\nVisibility checks" },
    { title: "ESP", text: "Player boxes\nSkeletons\nDistance" },
    { title: "MISC", text: "Stream proof\nConfig system\nFast support" },
  ],
  media: "",
};

const productPlanOptions = [1, 2, 3, 4, 5, 6];
const productFeatureBlockOptions = [1, 2, 3, 4, 5, 6, 7, 8];

function productMediaLines(value) {
  return String(value || "")
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function featureLines(value) {
  return String(value || "")
    .split(/\r?\n/)
    .map((item) => item.trim().replace(/^[-*\u2022]\s+/, "").replace(/^\d+[.)]\s+/, ""))
    .filter(Boolean);
}

function isProductVideoPath(value) {
  return /\.(mp4|webm|ogg|mov|m4v)$/i.test(value || "");
}

function getProductCreatorIssues(form) {
  const issues = [];
  const media = productMediaLines(form.media);
  const filledPlans = form.plans.filter((plan) => String(plan.plan || "").trim() || String(plan.price || "").trim() || String(plan.url || "").trim());
  const missingCheckoutCount = filledPlans.filter((plan) => !String(plan.url || "").trim()).length;
  const featureLineGroups = form.featureBlocks.map((block) => featureLines(block.text));
  const hasFeatureLine = featureLineGroups.some((lines) => lines.length > 0);

  if (!String(form.name || "").trim()) issues.push({ level: "error", message: "Product name is required." });
  if (!String(form.slug || "").trim()) issues.push({ level: "error", message: "URL slug is required." });
  if (media.length === 0) issues.push({ level: "error", message: "Add at least one media path so the generated page has a preview/player." });
  if (!hasFeatureLine) issues.push({ level: "error", message: "Add at least one feature line." });
  if (missingCheckoutCount > 0) issues.push({ level: "error", message: `${missingCheckoutCount} purchase button${missingCheckoutCount > 1 ? "s are" : " is"} missing a checkout URL.` });
  if (!String(form.logo || "").trim()) issues.push({ level: "warning", message: "No logo selected. Category cards will look weaker." });
  if (media.length > 0 && !media.some(isProductVideoPath)) issues.push({ level: "warning", message: "Only image media selected. The generated page will not show the video player unless you add a video file." });
  if (media.some((item) => !item.startsWith("/"))) issues.push({ level: "warning", message: "Some media paths do not start with /. Public assets usually should." });
  if (form.logo && !String(form.logo).startsWith("/")) issues.push({ level: "warning", message: "Logo path does not start with /. Public assets usually should." });
  if (featureLineGroups.some((lines) => lines.some((line) => line.length > 170))) issues.push({ level: "warning", message: "Some feature lines are very long. Split descriptions into shorter bullets for cleaner generated pages." });

  return issues;
}

function ProductCreatorPreview({ form, mediaPaths, issues }) {
  const selectedGame = String(form.dataFile || "").replace(/\.json$/i, "");
  const firstMedia = mediaPaths[0];
  const errorCount = issues.filter((issue) => issue.level === "error").length;
  const warningCount = issues.filter((issue) => issue.level === "warning").length;

  return (
    <aside className="rounded-lg border border-purple-400/15 bg-black/20 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-2xl font-extrabold text-white">Page preview</h3>
          <p className="text-base text-gray-400">Quick check before creation.</p>
        </div>
        <span className={`rounded-md border px-2 py-1 text-sm font-extrabold ${errorCount ? "border-red-400/35 bg-red-500/10 text-red-100" : "border-green-400/30 bg-green-500/10 text-green-100"}`}>
          {errorCount ? `${errorCount} blocked` : "Ready"}
        </span>
      </div>

      <div className="overflow-hidden rounded-lg border border-purple-400/20 bg-gray-950/65">
        <div className="aspect-video bg-black">
          {firstMedia ? (
            isProductVideoPath(firstMedia) ? (
              <video src={firstMedia} className="h-full w-full object-contain" muted preload="metadata" />
            ) : (
              <img src={firstMedia} alt="" className="h-full w-full object-contain" />
            )
          ) : (
            <div className="flex h-full items-center justify-center text-base font-bold text-gray-500">
              No media
            </div>
          )}
        </div>
        <div className="p-3">
          <div className="flex items-start gap-3">
            {form.logo ? (
              <img src={form.logo} alt="" className="h-12 w-12 shrink-0 rounded-lg border border-purple-400/20 object-cover" />
            ) : (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-purple-400/20 bg-black/35 text-gray-500">
                <ImagePlus size={20} />
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-xl font-extrabold text-white">{form.pageName || form.name || "Product name"}</p>
              <p className="truncate text-base font-bold text-gray-400">/{selectedGame || "game"} / {form.slug || "slug"}</p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-md border border-purple-400/20 bg-purple-500/10 px-2 py-1 text-sm font-bold text-purple-100">
              {mediaPaths.length} media
            </span>
            <span className="rounded-md border border-purple-400/20 bg-purple-500/10 px-2 py-1 text-sm font-bold text-purple-100">
              {form.plans.length} buttons
            </span>
            <span className="rounded-md border border-purple-400/20 bg-purple-500/10 px-2 py-1 text-sm font-bold text-purple-100">
              {form.featureBlocks.length} sections
            </span>
          </div>
        </div>
      </div>

      {issues.length > 0 && (
        <div className="mt-3 space-y-2">
          {issues.map((issue) => (
            <p
              key={issue.message}
              className={`rounded-md border px-3 py-2 text-sm font-bold ${
                issue.level === "error"
                  ? "border-red-400/25 bg-red-500/10 text-red-100"
                  : "border-yellow-400/25 bg-yellow-500/10 text-yellow-100"
              }`}
            >
              {issue.message}
            </p>
          ))}
          {warningCount > 0 && errorCount === 0 && (
            <p className="text-sm font-bold text-gray-500">Warnings do not block creation.</p>
          )}
        </div>
      )}
    </aside>
  );
}

function ProductCreatorPanel() {
  const [games, setGames] = useState([]);
  const [form, setForm] = useState(emptyProductForm);
  const [message, setMessage] = useState("");
  const [created, setCreated] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const mediaPaths = useMemo(() => productMediaLines(form.media), [form.media]);
  const creatorIssues = useMemo(() => getProductCreatorIssues(form), [form]);
  const blockingIssues = creatorIssues.filter((issue) => issue.level === "error");

  useEffect(() => {
    apiRequest("/api/admin/product-options")
      .then((data) => {
        const options = data.games || [];
        setGames(options);
        if (options.length > 0) {
          setForm((current) => ({ ...current, dataFile: current.dataFile || options[0].dataFile }));
        }
      })
      .catch((error) => setMessage(error.message));
  }, []);

  const updateField = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === "name" && (!current.slug || current.slug === slugFromName(current.name))) {
        next.slug = slugFromName(value);
      }
      if (field === "name" && (!current.catalogName || current.catalogName === String(current.name || "").toUpperCase())) {
        next.catalogName = String(value || "").toUpperCase();
      }
      if (field === "name" && (!current.pageName || current.pageName === pageNameFromName(current.name))) {
        next.pageName = pageNameFromName(value);
      }
      return next;
    });
  };

  const updateRequirement = (field, value) => {
    setForm((current) => ({
      ...current,
      requirements: {
        ...current.requirements,
        [field]: value,
      },
    }));
  };

  const toggleOs = (version) => {
    setForm((current) => ({
      ...current,
      os: current.os.includes(version)
        ? current.os.filter((item) => item !== version)
        : [...current.os, version],
    }));
  };

  const setPlanCount = (count) => {
    setForm((current) => {
      const nextPlans = [...current.plans];
      while (nextPlans.length < count) {
        const index = nextPlans.length;
        nextPlans.push({ plan: index === 0 ? "1 Day" : `${index + 1} Days`, price: "$0", url: "" });
      }
      return { ...current, plans: nextPlans.slice(0, count) };
    });
  };

  const updatePlan = (index, field, value) => {
    setForm((current) => ({
      ...current,
      plans: current.plans.map((plan, planIndex) => (
        planIndex === index ? { ...plan, [field]: value } : plan
      )),
    }));
  };

  const setFeatureBlockCount = (count) => {
    setForm((current) => {
      const nextBlocks = [...current.featureBlocks];
      while (nextBlocks.length < count) {
        nextBlocks.push({ title: "FEATURES", text: "" });
      }
      return { ...current, featureBlocks: nextBlocks.slice(0, count) };
    });
  };

  const updateFeatureBlock = (index, field, value) => {
    setForm((current) => ({
      ...current,
      featureBlocks: current.featureBlocks.map((block, blockIndex) => (
        blockIndex === index ? { ...block, [field]: value } : block
      )),
    }));
  };

  const selectProductAssets = async (kind, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    setMessage("");

    try {
      const filenames = files.map((file) => productAssetNameFromFile(file));
      const data = await apiRequest("/api/admin/product-asset-paths", {
        method: "POST",
        body: JSON.stringify({ kind, filenames }),
      });
      const paths = (data.files || []).map((file) => file.path).filter(Boolean);
      if (paths.length === 0) {
        throw new Error("No matching public asset path found.");
      }

      if (kind === "logo") {
        updateField("logo", paths[0]);
      } else {
        setForm((current) => ({
          ...current,
          media: mergeMultilinePaths(current.media, paths),
        }));
      }
      setMessage(`${paths.length} existing asset path${paths.length > 1 ? "s" : ""} selected. No file was copied.`);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const createProduct = async (event) => {
    event.preventDefault();
    setMessage("");
    setCreated(null);

    const hasFeatureLine = form.featureBlocks.some((block) => String(block.text || "").trim());
    if (!hasFeatureLine) {
      setMessage("Add at least one feature line in one Feature text block.");
      return;
    }
    if (blockingIssues.length > 0) {
      setMessage(blockingIssues[0].message);
      return;
    }

    setIsCreating(true);

    try {
      const data = await apiRequest("/api/admin/products", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setCreated(data);
      setMessage(`Product created: ${data.product?.name || form.name}`);
      setForm((current) => ({ ...emptyProductForm, dataFile: current.dataFile }));
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <section className="rounded-lg border border-purple-400/20 bg-gray-950/55 p-5 shadow-xl backdrop-blur-xl">
      <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-3xl font-extrabold">Product page creator</h2>
          <p className="text-lg text-gray-400">Create the catalog entry and React product page from the admin panel.</p>
        </div>
        {created?.previewUrl && (
          <Link to={created.previewUrl} className="inline-flex items-center gap-2 rounded-lg border border-green-400/30 bg-green-500/10 px-4 py-2 text-lg font-bold text-green-100 transition hover:bg-green-500/20">
            <ExternalLink size={18} />
            Preview page
          </Link>
        )}
      </div>

      <form onSubmit={createProduct} className="grid gap-4">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="grid gap-4 md:grid-cols-2">
          <label className="grid gap-2 text-lg font-bold text-gray-200">
            Game
            <select value={form.dataFile} onChange={(event) => updateField("dataFile", event.target.value)} className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400">
              {games.map((game) => (
                <option key={game.dataFile} value={game.dataFile}>{game.name} / {game.dataFile}</option>
              ))}
            </select>
          </label>

          <label className="grid gap-2 text-lg font-bold text-gray-200">
            Product name
            <input value={form.name} onChange={(event) => updateField("name", event.target.value)} placeholder="Example: ARCANE APEX" className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400" />
          </label>

          <label className="grid gap-2 text-lg font-bold text-gray-200">
            Catalog name
            <input value={form.catalogName} onChange={(event) => updateField("catalogName", event.target.value)} placeholder="ARCANE" className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400" />
            <span className="text-sm text-gray-500">Shown on category pages. Auto uppercase, editable for exceptions.</span>
          </label>

          <label className="grid gap-2 text-lg font-bold text-gray-200">
            Product page name
            <input value={form.pageName} onChange={(event) => updateField("pageName", event.target.value)} placeholder="Arcane" className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400" />
            <span className="text-sm text-gray-500">Shown in the product page title. Editable for exceptions.</span>
          </label>

          <label className="grid gap-2 text-lg font-bold text-gray-200">
            URL slug
            <input value={form.slug} onChange={(event) => updateField("slug", slugFromName(event.target.value))} placeholder="arcaneapex" className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400" />
            <span className="text-sm text-gray-500">Final URL: /games/{form.slug || "slug"}</span>
          </label>

          <label className="grid gap-2 text-lg font-bold text-gray-200">
            Status
            <select value={form.status} onChange={(event) => updateField("status", event.target.value)} className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400">
              {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
          </label>

          <div className="grid gap-2 text-lg font-bold text-gray-200">
            Windows
            <div className="flex gap-2">
              {["10", "11"].map((version) => (
                <button key={version} type="button" onClick={() => toggleOs(version)} className={`rounded-lg border px-4 py-3 text-xl font-bold ${form.os.includes(version) ? "border-purple-300/60 bg-purple-600/30 text-white" : "border-purple-400/20 bg-black/30 text-purple-100"}`}>
                  Windows {version}
                </button>
              ))}
            </div>
          </div>

          <label className="grid gap-2 text-lg font-bold text-gray-200">
            Logo path
            <input value={form.logo} onChange={(event) => updateField("logo", event.target.value)} placeholder="/images/dev/arcane.jpg" className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400" />
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={(event) => {
                selectProductAssets("logo", event.target.files);
                event.target.value = "";
              }}
              className="admin-field rounded-lg border border-purple-400/20 bg-black/30 px-4 py-3 text-base text-purple-100 file:mr-3 file:rounded-md file:border-0 file:bg-purple-600 file:px-3 file:py-2 file:text-sm file:font-bold file:text-white hover:file:bg-purple-500 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm text-gray-500">Selects an existing file from /images/dev without copying it.</span>
          </label>
          </div>

          <ProductCreatorPreview form={form} mediaPaths={mediaPaths} issues={creatorIssues} />
        </div>

        <div className="rounded-lg border border-purple-400/15 bg-black/20 p-4">
          <div className="mb-4">
            <h3 className="text-2xl font-extrabold text-white">System Requirements</h3>
            <p className="text-lg text-gray-400">These values are rendered in the same table as the real product pages.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <label className="grid gap-2 text-lg font-bold text-gray-200">
              Windows versions
              <input value={form.requirements.windowsVersions} onChange={(event) => updateRequirement("windowsVersions", event.target.value)} placeholder="21H2, 22H2, 23H2" className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400" />
            </label>

            <label className="grid gap-2 text-lg font-bold text-gray-200">
              Platform
              <input value={form.requirements.platform} onChange={(event) => updateRequirement("platform", event.target.value)} list="product-platform-options" placeholder="Steam" className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400" />
            </label>

            <label className="grid gap-2 text-lg font-bold text-gray-200">
              CPU Compatibility
              <input value={form.requirements.cpu} onChange={(event) => updateRequirement("cpu", event.target.value)} placeholder="Intel and AMD" className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400" />
            </label>

            <label className="grid gap-2 text-lg font-bold text-gray-200">
              GPU Compatibility
              <input value={form.requirements.gpu} onChange={(event) => updateRequirement("gpu", event.target.value)} placeholder="Nvidia and AMD" className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400" />
            </label>

            <label className="grid gap-2 text-lg font-bold text-gray-200">
              Game mode
              <select value={form.requirements.gameMode} onChange={(event) => updateRequirement("gameMode", event.target.value)} className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400">
                <option value="Fullscreen/Windowed">Fullscreen/Windowed</option>
                <option value="Borderless/Windowed">Borderless/Windowed</option>
                <option value="Fullscreen/Borderless">Fullscreen/Borderless</option>
              </select>
            </label>

            <label className="grid gap-2 text-lg font-bold text-gray-200">
              Spoofer
              <select value={form.requirements.spoofer} onChange={(event) => updateRequirement("spoofer", event.target.value)} className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400">
                <option value="None">None</option>
                <option value="Built-in">Built-in</option>
              </select>
            </label>
          </div>

          <datalist id="product-platform-options">
            <option value="Steam" />
            <option value="Battle.net" />
            <option value="Epic Games" />
            <option value="Riot Games" />
            <option value="Ubisoft Connect" />
            <option value="PC" />
          </datalist>
        </div>

        <div className="rounded-lg border border-purple-400/15 bg-black/20 p-4">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-2xl font-extrabold text-white">Purchase buttons</h3>
              <p className="text-lg text-gray-400">Each button needs its own duration, price and checkout link.</p>
            </div>
            <label className="flex items-center gap-3 text-lg font-bold text-gray-200">
              Buttons
              <select value={form.plans.length} onChange={(event) => setPlanCount(Number(event.target.value))} className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-3 py-2 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400">
                {productPlanOptions.map((count) => <option key={count} value={count}>{count}</option>)}
              </select>
            </label>
          </div>

          <div className="grid gap-3">
            {form.plans.map((plan, index) => (
              <div key={`plan-${index}`} className="grid gap-3 rounded-lg border border-purple-400/10 bg-gray-950/45 p-3 md:grid-cols-[160px_140px_1fr]">
                <label className="grid gap-1 text-base font-bold text-gray-300">
                  Duration
                  <input value={plan.plan} onChange={(event) => updatePlan(index, "plan", event.target.value)} placeholder="1 Day" className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-3 py-2 text-lg text-white outline-none focus:ring-2 focus:ring-purple-400" />
                </label>
                <label className="grid gap-1 text-base font-bold text-gray-300">
                  Price
                  <input value={plan.price} onChange={(event) => updatePlan(index, "price", event.target.value)} placeholder="$2.15" className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-3 py-2 text-lg text-white outline-none focus:ring-2 focus:ring-purple-400" />
                </label>
                <label className="grid gap-1 text-base font-bold text-gray-300">
                  Checkout URL
                  <input value={plan.url} onChange={(event) => updatePlan(index, "url", event.target.value)} placeholder="https://www.oplata.info/..." className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-3 py-2 text-lg text-white outline-none focus:ring-2 focus:ring-purple-400" />
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-purple-400/15 bg-black/20 p-4">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-2xl font-extrabold text-white">Feature blocks</h3>
                <p className="text-lg text-gray-400">Create the same feature sections used by the real pages.</p>
              </div>
              <label className="flex items-center gap-3 text-lg font-bold text-gray-200">
                Blocks
                <select value={form.featureBlocks.length} onChange={(event) => setFeatureBlockCount(Number(event.target.value))} className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-3 py-2 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400">
                  {productFeatureBlockOptions.map((count) => <option key={count} value={count}>{count}</option>)}
                </select>
              </label>
            </div>

            <div className="grid gap-3">
              {form.featureBlocks.map((block, blockIndex) => (
                <div key={`feature-block-${blockIndex}`} className="rounded-lg border border-purple-400/10 bg-gray-950/45 p-3">
                  <label className="grid gap-1 text-base font-bold text-gray-300">
                    Section title
                    <input value={block.title} onChange={(event) => updateFeatureBlock(blockIndex, "title", event.target.value)} placeholder="AIMBOT" className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-3 py-2 text-lg text-white outline-none focus:ring-2 focus:ring-purple-400" />
                  </label>
                  <label className="mt-3 grid gap-1 text-base font-bold text-gray-300">
                    Feature text
                    <textarea value={block.text || ""} onChange={(event) => updateFeatureBlock(blockIndex, "text", event.target.value)} rows={7} placeholder={"Feature one\nFeature two\nFeature three"} className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-3 py-2 text-lg text-white outline-none focus:ring-2 focus:ring-purple-400" />
                    <span className="text-sm text-gray-500">Use one short feature per line. Bullets like - or 1. are cleaned automatically.</span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <label className="grid gap-2 text-lg font-bold text-gray-200">
            Media paths, one per line
            <textarea value={form.media} onChange={(event) => updateField("media", event.target.value)} rows={6} placeholder="/videos/apex/demo.webp" className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400" />
            <input
              type="file"
              multiple
              accept="image/*,video/*,.mp4,.webm,.ogg,.mov,.m4v"
              onChange={(event) => {
                selectProductAssets("media", event.target.files);
                event.target.value = "";
              }}
              className="admin-field rounded-lg border border-purple-400/20 bg-black/30 px-4 py-3 text-base text-purple-100 file:mr-3 file:rounded-md file:border-0 file:bg-purple-600 file:px-3 file:py-2 file:text-sm file:font-bold file:text-white hover:file:bg-purple-500 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm text-gray-500">Finds the selected existing files under /videos without copying them.</span>
          </label>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {message && <p className="rounded-lg border border-purple-400/20 bg-black/30 px-4 py-3 text-lg text-purple-100">{message}</p>}
          <button type="submit" disabled={isCreating || !form.dataFile || blockingIssues.length > 0} className="inline-flex items-center justify-center gap-2 rounded-lg border border-green-400/35 bg-green-600/25 px-5 py-3 text-xl font-extrabold text-green-50 transition hover:bg-green-600/35 disabled:cursor-not-allowed disabled:opacity-45">
            <Save size={20} />
            {isCreating ? "Creating..." : "Create product page"}
          </button>
        </div>
      </form>
    </section>
  );
}

function AdminTable({
  rows,
  pending,
  pendingNotes,
  selectedKeys,
  duplicateKeys,
  issueKeys,
  compactMode,
  onToggleRow,
  onToggleAllVisible,
  onChangeStatus,
  onChangeNote,
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-purple-400/20 bg-gray-950/65 p-8 text-center text-xl text-gray-300">
        No products found.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-purple-400/20 bg-gray-950/65 shadow-xl">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left">
          <thead className="bg-black/35 text-gray-300">
            <tr>
              <th className="px-4 py-3 text-lg">
                <button
                  type="button"
                  onClick={onToggleAllVisible}
                  className="inline-flex items-center text-purple-100 hover:text-purple-300"
                  aria-label="Toggle visible rows"
                >
                  {rows.every((row) => selectedKeys.has(rowKey(row))) ? (
                    <CheckSquare size={22} />
                  ) : (
                    <Square size={22} />
                  )}
                </button>
              </th>
              <th className="px-4 py-3 text-lg">Game</th>
              <th className="px-4 py-3 text-lg">Product</th>
              <th className="px-4 py-3 text-lg">Price</th>
              <th className="px-4 py-3 text-lg">Status</th>
              <th className="px-4 py-3 text-lg">Note</th>
              <th className="px-4 py-3 text-lg">Updated</th>
              <th className="px-4 py-3 text-lg">Open</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-purple-400/10">
            {rows.map((row) => {
              const key = rowKey(row);
              const currentStatus = pending[key] || row.status;
              const isChanged = pending[key] && pending[key] !== row.status;
              const noteValue = Object.prototype.hasOwnProperty.call(pendingNotes, key) ? pendingNotes[key] : row.note || "";
              const isNoteChanged = noteValue !== (row.note || "");
              const isSelected = selectedKeys.has(key);
              const hasDuplicate = duplicateKeys.has(key);
              const hasIssue = issueKeys.has(key);

              return (
                <tr
                  key={key}
                  className={`${STATUS_ROW_CLASSES[currentStatus] || "hover:bg-purple-500/5"} ${
                    isSelected ? "outline outline-1 outline-purple-400/45" : ""
                  }`}
                >
                  <td className={`${compactMode ? "px-3 py-2" : "px-4 py-3"} align-middle`}>
                    <button
                      type="button"
                      onClick={() => onToggleRow(key)}
                      className="inline-flex items-center text-purple-100 hover:text-purple-300"
                      aria-label={`Select ${row.name}`}
                    >
                      {isSelected ? <CheckSquare size={22} /> : <Square size={22} />}
                    </button>
                  </td>
                  <td className={`${compactMode ? "px-3 py-2" : "px-4 py-3"} text-lg text-purple-200`}>{row.gameName}</td>
                  <td className={compactMode ? "px-3 py-2" : "px-4 py-3"}>
                    <p className={`${compactMode ? "text-lg" : "text-xl"} font-bold text-white`}>{row.name}</p>
                    <p className="text-sm text-gray-500">
                      {row.dataFile}
                      {isChanged && (
                        <span className="ml-2 rounded border border-yellow-400/30 bg-yellow-500/10 px-2 py-0.5 text-yellow-100">
                          pending: {row.status} -&gt; {currentStatus}
                        </span>
                      )}
                      {isNoteChanged && (
                        <span className="ml-2 rounded border border-yellow-400/30 bg-yellow-500/10 px-2 py-0.5 text-yellow-100">
                          note pending
                        </span>
                      )}
                      {hasDuplicate && (
                        <span className="ml-2 rounded border border-red-400/30 bg-red-500/10 px-2 py-0.5 text-red-100">
                          duplicate
                        </span>
                      )}
                      {hasIssue && (
                        <span className="ml-2 rounded border border-orange-400/30 bg-orange-500/10 px-2 py-0.5 text-orange-100">
                          issue
                        </span>
                      )}
                    </p>
                  </td>
                  <td className={`${compactMode ? "px-3 py-2" : "px-4 py-3"} text-lg text-gray-300`}>{row.price || "-"}</td>
                  <td className={`${compactMode ? "px-3 py-2" : "px-4 py-3"} min-w-[190px]`}>
                    <StatusSelect
                      value={currentStatus}
                      onChange={(status) => onChangeStatus(row, status)}
                    />
                  </td>
                  <td className={`${compactMode ? "px-3 py-2" : "px-4 py-3"} min-w-[220px]`}>
                    <textarea
                      value={noteValue}
                      onChange={(event) => onChangeNote(row, event.target.value)}
                      rows={compactMode ? 1 : 2}
                      maxLength={500}
                      className="admin-field w-full resize-y rounded-lg border border-purple-400/20 bg-black/35 px-3 py-2 text-base text-white outline-none focus:ring-2 focus:ring-purple-400"
                      placeholder="Internal note"
                    />
                  </td>
                  <td className={`${compactMode ? "px-3 py-2" : "px-4 py-3"} text-base text-gray-400`}>
                    {formatDate(row.lastUpdatedAt)}
                  </td>
                  <td className={`${compactMode ? "px-3 py-2" : "px-4 py-3"}`}>
                    {row.link ? (
                      <a
                        href={row.link}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center rounded-lg border border-purple-400/25 bg-black/30 p-2 text-purple-100 transition hover:bg-purple-500/15"
                        aria-label={`Open ${row.name}`}
                      >
                        <ExternalLink size={18} />
                      </a>
                    ) : (
                      <span className="text-gray-600">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminStatusPanel() {
  useNoIndex();

  const [authenticated, setAuthenticated] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedGame, setSelectedGame] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [advancedFilter, setAdvancedFilter] = useState("all");
  const [presetGame, setPresetGame] = useState("all");
  const [presetStatus, setPresetStatus] = useState("undetected");
  const [compactMode, setCompactMode] = useState(false);
  const [activeView, setActiveView] = useState("dashboard");
  const [pending, setPending] = useState({});
  const [pendingNotes, setPendingNotes] = useState({});
  const [statusChangeMessage, setStatusChangeMessage] = useState("");
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [auditRows, setAuditRows] = useState([]);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [validation, setValidation] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [message, setMessage] = useState("");

  const loadAuditRows = useCallback(async () => {
    const data = await apiRequest("/api/admin/audit?limit=8");
    setAuditRows(data.rows || []);
  }, []);

  const loadRows = useCallback(async () => {
    setMessage("");
    const [data, featuredData] = await Promise.all([
      apiRequest("/api/admin/statuses"),
      apiRequest("/api/admin/featured-products").catch(() => ({ items: [] })),
      loadAuditRows().catch(() => null),
    ]);
    setRows(data.rows || []);
    setErrors(data.errors || []);
    setFeaturedItems(featuredData.items || []);
    setPending({});
    setPendingNotes({});
    setStatusChangeMessage("");
    setSelectedKeys(new Set());
  }, [loadAuditRows]);

  useEffect(() => {
    apiRequest("/api/admin/session")
      .then((data) => {
        setAuthenticated(Boolean(data.authenticated));
        if (data.authenticated) return loadRows();
        return null;
      })
      .catch(() => setAuthenticated(false))
      .finally(() => setIsCheckingSession(false));
  }, [loadRows]);

  const duplicateKeys = useMemo(() => {
    const links = new Map();
    rows.forEach((row) => {
      const link = (row.link || "").trim().toLowerCase();
      if (!link) return;
      links.set(link, [...(links.get(link) || []), rowKey(row)]);
    });

    const duplicates = new Set();
    links.forEach((keys) => {
      if (keys.length > 1) keys.forEach((key) => duplicates.add(key));
    });
    return duplicates;
  }, [rows]);

  const issueKeys = useMemo(() => {
    const keys = new Set();
    (validation?.issues || []).forEach((issue) => {
      if (issue.index >= 0) keys.add(`${issue.dataFile}:${issue.index}`);
    });
    return keys;
  }, [validation]);

  const recentlyChangedCutoff = useMemo(() => Date.now() - 7 * 24 * 60 * 60 * 1000, []);

  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesQuery =
        row.name.toLowerCase().includes(normalizedQuery) ||
        row.gameName.toLowerCase().includes(normalizedQuery) ||
        row.dataFile.toLowerCase().includes(normalizedQuery);
      const key = rowKey(row);
      const currentStatus = pending[key] || row.status;
      const matchesGame = selectedGame === "all" || row.gameName === selectedGame;
      const matchesStatus = selectedStatus === "all" || currentStatus === selectedStatus;
      const matchesAdvanced =
        advancedFilter === "all" ||
        (advancedFilter === "pending" && (pending[key] || Object.prototype.hasOwnProperty.call(pendingNotes, key))) ||
        (advancedFilter === "missingLogo" && !row.logo) ||
        (advancedFilter === "missingPrice" && !row.price) ||
        (advancedFilter === "duplicateLink" && duplicateKeys.has(key)) ||
        (advancedFilter === "recentlyChanged" && row.lastUpdatedAt && new Date(row.lastUpdatedAt).getTime() >= recentlyChangedCutoff) ||
        (advancedFilter === "validationIssues" && issueKeys.has(key));
      return matchesQuery && matchesGame && matchesStatus && matchesAdvanced;
    });
  }, [advancedFilter, duplicateKeys, issueKeys, pending, pendingNotes, query, recentlyChangedCutoff, rows, selectedGame, selectedStatus]);

  const gameOptions = useMemo(() => {
    return Array.from(new Set(rows.map((row) => row.gameName))).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [rows]);

  const hasPendingChanges = Object.keys(pending).length > 0 || Object.keys(pendingNotes).length > 0;

  const handleLogin = async () => {
    setAuthenticated(true);
    await loadRows();
  };

  const logout = async () => {
    await apiRequest("/api/admin/logout", { method: "POST" });
    setAuthenticated(false);
    setRows([]);
    setSelectedKeys(new Set());
    setAuditRows([]);
    setPending({});
    setPendingNotes({});
  };

  const changeStatus = (row, status) => {
    const key = rowKey(row);
    setMessage("");
    setPending((prev) => {
      const next = { ...prev };
      if (status === row.status) {
        delete next[key];
      } else {
        next[key] = status;
      }
      return next;
    });
  };

  const changeNote = (row, note) => {
    const key = rowKey(row);
    setMessage("");
    setPendingNotes((prev) => {
      const next = { ...prev };
      if (note === (row.note || "")) {
        delete next[key];
      } else {
        next[key] = note;
      }
      return next;
    });
  };

  const toggleRowSelection = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const visibleKeys = filteredRows.map(rowKey);
      const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((key) => next.has(key));

      visibleKeys.forEach((key) => {
        if (allVisibleSelected) {
          next.delete(key);
        } else {
          next.add(key);
        }
      });

      return next;
    });
  };

  const selectVisible = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      filteredRows.forEach((row) => next.add(rowKey(row)));
      return next;
    });
  };

  const applyBulkStatus = (status) => {
    setMessage("");
    const rowsByKey = new Map(rows.map((row) => [rowKey(row), row]));

    setPending((prev) => {
      const next = { ...prev };
      selectedKeys.forEach((key) => {
        const row = rowsByKey.get(key);
        if (!row) return;

        if (status === row.status) {
          delete next[key];
        } else {
          next[key] = status;
        }
      });
      return next;
    });
  };

  const applyVisibleStatus = (status) => {
    setMessage("");
    setPending((prev) => {
      const next = { ...prev };
      filteredRows.forEach((row) => {
        const key = rowKey(row);
        if (status === row.status) {
          delete next[key];
        } else {
          next[key] = status;
        }
      });
      return next;
    });
  };

  const applyGamePreset = () => {
    if (presetGame === "all") {
      setMessage("Choose a game before applying a preset.");
      return;
    }

    const matchingRows = rows.filter((row) => row.gameName === presetGame);
    if (matchingRows.length === 0) return;

    const confirmed = window.confirm(`Set ${matchingRows.length} ${presetGame} products to ${presetStatus}?`);
    if (!confirmed) return;

    setMessage("");
    setPending((prev) => {
      const next = { ...prev };
      matchingRows.forEach((row) => {
        const key = rowKey(row);
        if (presetStatus === row.status) {
          delete next[key];
        } else {
          next[key] = presetStatus;
        }
      });
      return next;
    });
  };

  const resetPending = () => {
    setPending({});
    setPendingNotes({});
    setStatusChangeMessage("");
    setMessage("Pending changes cleared.");
  };

  const savePending = async () => {
    const rowsByKey = new Map(rows.map((row) => [rowKey(row), row]));
    const discordNote = statusChangeMessage.trim();
    const changes = Object.entries(pending)
      .map(([key, status]) => {
        const row = rowsByKey.get(key);
        if (!row || row.status === status) return null;

        return {
          dataFile: row.dataFile,
          index: row.index,
          name: row.name,
          link: row.link,
          status,
          ...(discordNote ? { discordNote } : {}),
        };
      })
      .filter(Boolean);
    const noteChanges = Object.entries(pendingNotes)
      .map(([key, note]) => {
        const row = rowsByKey.get(key);
        if (!row || (row.note || "") === note) return null;

        return {
          dataFile: row.dataFile,
          index: row.index,
          name: row.name,
          link: row.link,
          note,
        };
      })
      .filter(Boolean);

    if (changes.length === 0 && noteChanges.length === 0) {
      setPending({});
      setPendingNotes({});
      setMessage("No changes to save.");
      return;
    }

    setIsSaving(true);
    setMessage("");

    try {
      const [statusData, noteData] = await Promise.all([
        changes.length
          ? apiRequest("/api/admin/statuses", {
              method: "PATCH",
              body: JSON.stringify({ changes }),
            })
          : Promise.resolve({ updated: [] }),
        noteChanges.length
          ? apiRequest("/api/admin/notes", {
              method: "PATCH",
              body: JSON.stringify({ changes: noteChanges }),
            })
          : Promise.resolve({ updated: [] }),
      ]);

      const updatedRows = statusData.updated || [];
      const updatedNotes = noteData.updated || [];
      const updatedAt = new Date().toISOString();
      setRows((currentRows) =>
        currentRows.map((item) => {
          const updated = updatedRows.find(
            (row) => row.dataFile === item.dataFile && row.index === item.index
          );
          const noteUpdated = updatedNotes.find(
            (row) => row.dataFile === item.dataFile && row.index === item.index
          );
          return updated || noteUpdated
            ? {
                ...item,
                ...(updated ? { status: updated.status } : {}),
                ...(noteUpdated ? { note: noteUpdated.note } : {}),
                lastUpdatedAt: updatedAt,
              }
            : item;
        })
      );
      setPending({});
      setPendingNotes({});
      setSelectedKeys(new Set());
      if (updatedRows.length > 0) {
        setStatusChangeMessage("");
      }
      await loadAuditRows().catch(() => null);
      setMessage(`${updatedRows.length + updatedNotes.length} change${updatedRows.length + updatedNotes.length > 1 ? "s" : ""} saved.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const validateData = async () => {
    setIsValidating(true);
    setMessage("");
    try {
      const data = await apiRequest("/api/admin/validate");
      setValidation(data);
      setMessage(`${data.checked || 0} products checked, ${(data.issues || []).length} issue${(data.issues || []).length > 1 ? "s" : ""} found.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsValidating(false);
    }
  };

  const undoLastSave = async () => {
    const confirmed = window.confirm("Undo the latest saved status change?");
    if (!confirmed) return;

    setIsSaving(true);
    setMessage("");
    try {
      const data = await apiRequest("/api/admin/undo-last", { method: "POST" });
      await loadRows();
      await loadAuditRows().catch(() => null);
      setMessage(`${(data.updated || []).length} status change${(data.updated || []).length > 1 ? "s" : ""} undone.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const exportCsv = () => {
    const headers = ["game", "product", "status", "price", "link", "dataFile", "index", "note", "lastUpdatedAt"];
    const body = filteredRows.map((row) => {
      const key = rowKey(row);
      return [
        row.gameName,
        row.name,
        pending[key] || row.status,
        row.price,
        row.link,
        row.dataFile,
        row.index,
        Object.prototype.hasOwnProperty.call(pendingNotes, key) ? pendingNotes[key] : row.note,
        row.lastUpdatedAt,
      ].map(escapeCsv).join(",");
    });
    const csv = [headers.join(","), ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "ops-vault-export.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const pendingTotal = Object.keys(pending).length + Object.keys(pendingNotes).length;
  const statusCounts = rows.reduce((summary, row) => {
    summary[row.status] = (summary[row.status] || 0) + 1;
    return summary;
  }, {});
  const adminNavItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: Activity,
      meta: "Overview",
      tone: "purple",
    },
    {
      id: "chat",
      label: "Live chat",
      icon: MessageCircle,
      meta: "Customer support",
      tone: "purple",
    },
    {
      id: "status",
      label: "Status control",
      icon: ShieldCheck,
      meta: `${rows.length} products`,
      tone: "green",
    },
    {
      id: "visitors",
      label: "Active visitors",
      icon: Monitor,
      meta: "Live sessions",
      tone: "purple",
    },
    {
      id: "products",
      label: "Product creator",
      icon: ImagePlus,
      meta: "Catalog entry",
      tone: "purple",
    },
    {
      id: "featured",
      label: "Popular",
      icon: CheckSquare,
      meta: `${featuredItems.length}/8 home cards`,
      tone: "green",
    },
  ];

  const navButtonClass = (item) => {
    const isActive = activeView === item.id;
    if (item.tone === "green") {
      return isActive
        ? "border-green-300/60 bg-green-600/25 text-white shadow-[0_0_26px_rgba(34,197,94,0.14)]"
        : "border-green-400/20 bg-green-600/10 text-green-100 hover:border-green-300/45 hover:bg-green-600/20";
    }

    return isActive
      ? "border-purple-300/60 bg-purple-600/30 text-white shadow-[0_0_26px_rgba(168,85,247,0.16)]"
      : "border-purple-400/20 bg-white/[0.03] text-purple-100 hover:border-purple-300/45 hover:bg-purple-600/15";
  };

  if (isCheckingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white font-jersey">
        <div className="h-10 w-10 rounded-full border-2 border-purple-300 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!authenticated) {
    return <LoginPanel onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen px-4 py-8 text-white font-jersey">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="overflow-hidden rounded-lg border border-purple-400/20 bg-gray-950/70 shadow-xl backdrop-blur-xl">
          <div className="flex flex-col gap-5 p-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-4xl font-extrabold leading-none">Staff Control</h1>
                {hasPendingChanges && (
                  <span className="rounded-md border border-yellow-300/35 bg-yellow-500/10 px-3 py-1 text-base font-extrabold text-yellow-100">
                    {pendingTotal} unsaved
                  </span>
                )}
              </div>
              <p className="mt-2 max-w-2xl text-lg leading-snug text-gray-400">
                Live chat, visitors, product creation and status operations in one workspace.
              </p>
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-lg border border-purple-400/15 bg-black/25 px-3 py-2">
                  <p className="text-sm font-bold uppercase text-gray-500">Products</p>
                  <p className="text-2xl font-extrabold text-white">{rows.length}</p>
                </div>
                <div className="rounded-lg border border-green-400/20 bg-green-500/10 px-3 py-2">
                  <p className="text-sm font-bold uppercase text-green-200/70">Ready</p>
                  <p className="text-2xl font-extrabold text-green-100">{statusCounts.undetected || 0}</p>
                </div>
                <div className="rounded-lg border border-amber-400/20 bg-amber-500/10 px-3 py-2">
                  <p className="text-sm font-bold uppercase text-amber-200/70">Watch</p>
                  <p className="text-2xl font-extrabold text-amber-100">{(statusCounts.updating || 0) + (statusCounts.frozen || 0)}</p>
                </div>
                <div className="rounded-lg border border-red-400/20 bg-red-500/10 px-3 py-2">
                  <p className="text-sm font-bold uppercase text-red-200/70">Detected</p>
                  <p className="text-2xl font-extrabold text-red-100">{statusCounts.detected || 0}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 xl:justify-end">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-lg border border-purple-400/25 bg-black/25 px-4 py-2 text-lg font-bold text-purple-100 transition hover:bg-purple-600/20"
              >
                Back to site
              </Link>
              <button
                type="button"
                onClick={loadRows}
                className="inline-flex items-center gap-2 rounded-lg border border-purple-400/25 bg-purple-600/15 px-4 py-2 text-lg font-bold text-purple-200 transition hover:bg-purple-600/25"
              >
                <RefreshCw size={18} />
                Refresh
              </button>
              <button
                type="button"
                onClick={logout}
                className="inline-flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-600/10 px-4 py-2 text-lg font-bold text-red-200 transition hover:bg-red-600/20"
              >
                <LogOut size={18} />
                Logout
              </button>
            </div>
          </div>

          <nav className="grid border-t border-purple-400/15 bg-black/20 p-2 sm:grid-cols-2 xl:grid-cols-6" aria-label="Staff sections">
            {adminNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveView(item.id)}
                  className={`m-1 flex min-h-[4.5rem] items-center gap-3 rounded-lg border px-4 py-3 text-left transition ${navButtonClass(item)}`}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-white/10 bg-black/25">
                    <Icon size={20} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xl font-extrabold leading-tight">{item.label}</span>
                    <span className="block truncate text-base font-bold opacity-65">{item.meta}</span>
                  </span>
                </button>
              );
            })}
          </nav>
        </header>

        {activeView === "dashboard" && (
          <div className="space-y-3">
            <div className="border-b border-purple-400/20 pb-3">
              <h2 className="text-4xl font-extrabold">Dashboard</h2>
              <p className="text-lg text-gray-400">Operational overview for products, popular home cards and recent changes.</p>
            </div>
            <AdminDashboard
              rows={rows}
              auditRows={auditRows}
              featuredItems={featuredItems}
              statusCounts={statusCounts}
              pendingTotal={pendingTotal}
              hasPendingChanges={hasPendingChanges}
              onNavigate={setActiveView}
              onRefresh={loadRows}
            />
          </div>
        )}

        {activeView === "chat" && (
          <div className="space-y-3">
            <div className="border-b border-purple-400/20 pb-3">
              <h2 className="text-4xl font-extrabold">Live Chat</h2>
              <p className="text-lg text-gray-400">Answer customer messages and manage test conversations.</p>
            </div>
            <AdminChatPanel />
          </div>
        )}

        {activeView === "visitors" && (
          <div className="space-y-3">
            <div className="border-b border-purple-400/20 pb-3">
              <h2 className="text-4xl font-extrabold">Active Visitors</h2>
              <p className="text-lg text-gray-400">Monitor current sessions independently from live chat.</p>
            </div>
            <ActiveVisitorsPanel />
          </div>
        )}

        {activeView === "products" && (
          <div className="space-y-3">
            <div className="border-b border-purple-400/20 pb-3">
              <h2 className="text-4xl font-extrabold">Product Creator</h2>
              <p className="text-lg text-gray-400">Create product catalog entries and generated product pages.</p>
            </div>
            <ProductCreatorPanel />
          </div>
        )}

        {activeView === "featured" && (
          <div className="space-y-3">
            <div className="border-b border-purple-400/20 pb-3">
              <h2 className="text-4xl font-extrabold">Popular Products</h2>
              <p className="text-lg text-gray-400">Choose which products appear in the popular section on the home page.</p>
            </div>
            <FeaturedProductsPanel
              rows={rows}
              featuredItems={featuredItems}
              onFeaturedItemsChange={setFeaturedItems}
            />
          </div>
        )}

        {activeView === "status" && (
        <div className="space-y-5">
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-4xl font-extrabold">Status Control</h2>
              <p className="text-lg text-gray-400">
                {rows.length} products loaded
                {hasPendingChanges && (
                  <span className="ml-2 text-yellow-200">
                    {Object.keys(pending).length + Object.keys(pendingNotes).length} unsaved
                  </span>
                )}
              </p>
            </div>
          </div>

          <StatusSummary rows={rows} pending={pending} pendingNoteCount={Object.keys(pendingNotes).length} />

          <section className="rounded-lg border border-purple-400/20 bg-gray-950/55 p-4 shadow-xl backdrop-blur-xl">
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1fr_auto_auto_auto_auto] xl:items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search game, product, file..."
                  className="admin-field w-full rounded-lg border border-purple-400/20 bg-black/40 py-3 pl-11 pr-4 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>
              <select
                value={selectedGame}
                onChange={(event) => setSelectedGame(event.target.value)}
                className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="all">all games</option>
                {gameOptions.map((gameName) => (
                  <option key={gameName} value={gameName}>
                    {gameName}
                  </option>
                ))}
              </select>
              <select
                value={selectedStatus}
                onChange={(event) => setSelectedStatus(event.target.value)}
                className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="all">all statuses</option>
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <select
                value={advancedFilter}
                onChange={(event) => setAdvancedFilter(event.target.value)}
                className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="all">all checks</option>
                <option value="pending">pending only</option>
                <option value="missingLogo">missing logo</option>
                <option value="missingPrice">missing price</option>
                <option value="duplicateLink">duplicate link</option>
                <option value="recentlyChanged">changed last 7 days</option>
                <option value="validationIssues">validation issues</option>
              </select>
              <label className="inline-flex items-center justify-center gap-2 rounded-lg border border-purple-400/20 bg-black/30 px-4 py-3 text-xl text-purple-100">
                <input
                  type="checkbox"
                  checked={compactMode}
                  onChange={(event) => setCompactMode(event.target.checked)}
                  className="h-4 w-4 accent-purple-500"
                />
                Compact
              </label>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_auto]">
              <select
                value={presetGame}
                onChange={(event) => setPresetGame(event.target.value)}
                className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="all">choose game preset</option>
                {gameOptions.map((gameName) => (
                  <option key={gameName} value={gameName}>
                    {gameName}
                  </option>
                ))}
              </select>
              <select
                value={presetStatus}
                onChange={(event) => setPresetStatus(event.target.value)}
                className="admin-field rounded-lg border border-purple-400/20 bg-black/40 px-4 py-3 text-xl text-white outline-none focus:ring-2 focus:ring-purple-400"
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={applyGamePreset}
                className="rounded-lg border border-purple-400/25 bg-purple-600/15 px-4 py-3 text-xl font-bold text-purple-100 transition hover:bg-purple-500/20"
              >
                Apply game preset
              </button>
            </div>
          </section>

          <BulkActions
            selectedCount={selectedKeys.size}
            visibleCount={filteredRows.length}
            hasPending={hasPendingChanges}
            statusChangeMessage={statusChangeMessage}
            isSaving={isSaving}
            isValidating={isValidating}
            onApplyStatus={applyBulkStatus}
            onApplyVisibleStatus={applyVisibleStatus}
            onStatusChangeMessageChange={setStatusChangeMessage}
            onClearSelection={() => setSelectedKeys(new Set())}
            onSelectVisible={selectVisible}
            onResetPending={resetPending}
            onUndoLast={undoLastSave}
            onExportCsv={exportCsv}
            onValidate={validateData}
            onSave={savePending}
          />

          {message && (
            <div className="rounded-lg border border-purple-400/20 bg-gray-950/65 px-4 py-3 text-lg text-purple-100">
              <Save className="mr-2 inline" size={18} />
              {message}
            </div>
          )}

          {errors.length > 0 && (
            <div className="rounded-lg border border-yellow-400/35 bg-yellow-500/10 p-4 text-yellow-100">
              {errors.length} data source{errors.length > 1 ? "s" : ""} could not be loaded.
            </div>
          )}

          {validation && (
            <section className="rounded-lg border border-purple-400/20 bg-gray-950/55 p-4 shadow-xl backdrop-blur-xl">
              <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                <h2 className="text-2xl font-extrabold">Data validation</h2>
                <p className="text-lg text-gray-300">
                  {validation.checked} checked / {(validation.issues || []).length} issue{(validation.issues || []).length > 1 ? "s" : ""}
                </p>
              </div>
              {(validation.issues || []).length > 0 && (
                <div className="mt-3 grid gap-2 md:grid-cols-2">
                  {(validation.issues || []).slice(0, 12).map((issue, index) => (
                    <div key={`${issue.dataFile}-${issue.index}-${issue.type}-${index}`} className="rounded-lg border border-orange-400/20 bg-orange-500/10 px-3 py-2 text-orange-100">
                      <span className="font-bold">{issue.gameName || issue.dataFile}</span>
                      <span className="mx-2 text-orange-300">/</span>
                      <span>{issue.name || issue.dataFile}</span>
                      <span className="ml-2 text-orange-200">{issue.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          <AdminTable
            rows={filteredRows}
            pending={pending}
            pendingNotes={pendingNotes}
            selectedKeys={selectedKeys}
            duplicateKeys={duplicateKeys}
            issueKeys={issueKeys}
            compactMode={compactMode}
            onToggleRow={toggleRowSelection}
            onToggleAllVisible={toggleAllVisible}
            onChangeStatus={changeStatus}
            onChangeNote={changeNote}
          />

          <AuditPanel rows={auditRows} />
        </div>
        )}
      </div>
    </div>
  );
}
